<template>
  <el-dialog
    :visible.sync="dialogVisible"
    :title="isEdit ? '编辑组件' : '新增组件'"
    width="70%"
    :before-close="handleClose"
    append-to-body
    :modal-append-to-body="false"
    custom-class="unit-editor-dialog"
  >
    <div class="unit-editor-content">
      <el-form :model="formData" :rules="rules" ref="unitForm" label-width="120px">
        <!-- 基本信息 -->
        <el-card class="form-section">
          <div slot="header" class="card-header">
            <span>基本信息</span>
            <el-button v-if="!isEdit" size="small" type="success" icon="el-icon-download" @click="showImportDialog">
              导入组件数据
            </el-button>
          </div>
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="组件ID" prop="unitId">
                <el-input-number v-model="formData.unitId" :min="1" placeholder="组件ID"></el-input-number>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="组件类型" prop="unitType">
                <el-select v-model="formData.unitType" placeholder="请选择组件类型" @change="handleTypeChange">
                  <el-option v-for="item in UNIT_TYPES" :key="item.value" 
                    :label="item.label" :value="item.value">
                  </el-option>
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
          
          <el-form-item label="组件名称" prop="unitName">
            <el-input v-model="formData.unitName" placeholder="请输入组件名称（英文）"></el-input>
          </el-form-item>
          
          <el-form-item label="组件别名" prop="unitAka">
            <el-input v-model="formData.unitAka" placeholder="请输入组件别名（中文）"></el-input>
          </el-form-item>
          
          <el-form-item label="组件描述" prop="unitDesc">
            <el-input v-model="formData.unitDesc" type="textarea" :rows="2" placeholder="请输入组件描述"></el-input>
          </el-form-item>
          
          <el-form-item label="排序权重" prop="weight">
            <el-input-number v-model="formData.weight" :min="0" placeholder="排序权重"></el-input-number>
          </el-form-item>
        </el-card>

        <!-- 货币组件配置 -->
        <el-card v-if="formData.unitType === 1" header="货币组件配置" class="form-section">
          <el-form-item label="货币图标">
            <div style="display: flex; align-items: center; gap: 10px;">
              <UploadSingleImg v-model="formData.currencyUnit.icon" :on-upload="uploadCurrencyIcon" />
              <el-input v-model="formData.currencyUnit.icon" placeholder="请输入货币图标URL" style="flex: 1;"></el-input>
            </div>
          </el-form-item>
          
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="货币类型">
                <el-select v-model="formData.currencyUnit.type" placeholder="请选择货币类型">
                  <el-option 
                    v-for="item in CURRENCY_TYPES" 
                    :key="item.value" 
                    :value="item.value" 
                    :label="item.label">
                  </el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="显示倍率">
                <el-input-number v-model="formData.currencyUnit.showRate" :min="1" placeholder="显示倍率"></el-input-number>
              </el-form-item>
            </el-col>
          </el-row>

          <!-- 新增字段 -->
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="开启自动抽奖">
                <el-switch v-model="formData.currencyUnit.enableAutoDraw"></el-switch>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="开启货币赠送功能">
                <el-switch v-model="formData.currencyUnit.enableGive"></el-switch>
              </el-form-item>
            </el-col>
          </el-row>

          <!-- 仅在开启自动抽奖时显示 -->
          <div v-if="formData.currencyUnit.enableAutoDraw">
            <el-form-item label="自动抽奖的奖池组件ID">
              <el-input-number v-model="formData.currencyUnit.awardPoolId" :min="0" placeholder="自动抽奖的奖池组件ID"></el-input-number>
            </el-form-item>
          </div>

          <!-- 仅在开启货币赠送时显示 -->
          <div v-if="formData.currencyUnit.enableGive">
            <el-form-item label="单次赠送最大数量">
              <el-input-number v-model="formData.currencyUnit.giveConfig.onceMax" :min="1" placeholder="单次赠送货币数量最大值"></el-input-number>
              <div style="font-size: 12px; color: #999; margin-top: 4px;">注：必须大于0</div>
            </el-form-item>
          </div>
        </el-card>

        <!-- 奖池组件配置 -->
        <el-card v-if="formData.unitType === 2" header="奖池组件配置" class="form-section">
          <el-form-item label="消耗货币组件">
            <CurrencyUnitSelector 
              v-model="formData.awardPoolUnit.costUnitIds" 
              :all-units="allUnits"
              :multiple="true"
              placeholder="请选择消耗的货币组件">
            </CurrencyUnitSelector>
          </el-form-item>
          
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="次数汇率">
                <el-input-number v-model="formData.awardPoolUnit.timesRate" :min="1" 
                  placeholder="eg. 200 - 200后台金币:1次抽奖"></el-input-number>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="奖励Key">
                <reward-key-selector v-model="formData.awardPoolUnit.awardsKey" placeholder="请选择奖励Key"></reward-key-selector>
              </el-form-item>
            </el-col>
          </el-row>
          
          <!-- 三方充值分奖池配置 -->
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="开启三方充值分奖池">
                <el-switch v-model="formData.awardPoolUnit.enablePayPool"></el-switch>
                <div style="font-size: 12px; color: #999; margin-top: 4px;">
                  开启后，普通奖池使用【概率】，三方充值奖池使用【概率1】
                </div>
              </el-form-item>
            </el-col>
            <el-col :span="12" v-if="formData.awardPoolUnit.enablePayPool">
              <el-form-item label="近30天最小充值美金数">
                <el-input-number v-model="formData.awardPoolUnit.minPayUsd" :min="0" 
                  placeholder="近30天最小充值美金数" style="width: 100%"></el-input-number>
              </el-form-item>
            </el-col>
          </el-row>
        </el-card>

        <!-- 任务组件配置 -->
        <el-card v-if="formData.unitType === 3" header="任务组件配置" class="form-section">
          <el-row :gutter="20">
            <el-col :span="8">
              <el-form-item label="任务类型">
                <el-select v-model="formData.taskUnit.type" filterable placeholder="请选择任务类型">
                  <el-option v-for="item in taskConstants.taskTypes" :key="item.value" 
                    :label="item.name" :value="item.value">
                  </el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="任务方式">
                <el-select v-model="formData.taskUnit.mode" filterable placeholder="请选择任务方式">
                  <el-option v-for="item in taskConstants.taskModes" :key="item.value" 
                    :label="item.name" :value="item.value">
                  </el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="任务周期">
                <el-select v-model="formData.taskUnit.duration" filterable placeholder="请选择任务周期">
                  <el-option v-for="item in taskConstants.taskDurations" :key="item.value" 
                    :label="item.name" :value="item.value">
                  </el-option>
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
          
          <el-form-item label="校验参数">
            <el-input v-model="checkParamsSetStr" placeholder="请输入校验参数，多个用逗号分隔"></el-input>
          </el-form-item>
          
          <el-form-item label="自动下发奖励">
            <el-switch v-model="formData.taskUnit.autoGiveAward" @change="handleAutoGiveAwardChange"></el-switch>
          </el-form-item>
          
          <!-- 队伍任务配置 -->
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="是否为队伍任务">
                <el-switch 
                  v-model="formData.taskUnit.teamTask" 
                  :disabled="!formData.taskUnit.autoGiveAward"
                  @change="handleTeamTaskChange">
                </el-switch>
                <div v-if="!formData.taskUnit.autoGiveAward" style="font-size: 12px; color: #f56c6c; margin-top: 4px;">
                  请先开启自动下发奖励才能设置队伍任务
                </div>
              </el-form-item>
            </el-col>
            <el-col :span="12" v-if="formData.taskUnit.teamTask">
              <el-form-item label="队伍组件ID" prop="teamUnitId">
                <el-input-number v-model="formData.taskUnit.teamUnitId" :min="1" placeholder="请输入队伍组件ID"></el-input-number>
              </el-form-item>
            </el-col>
          </el-row>
          
          <!-- 单节点任务配置 -->
          <div class="task-config-section" v-if="formData.taskUnit.mode !== 2">
            <h4>单节点任务配置</h4>
            <el-row :gutter="20">
              <el-col :span="8">
                <el-form-item label="判定值">
                  <el-input-number v-model="formData.taskUnit.config.checkParams" :min="0"></el-input-number>
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item label="显示倍率">
                  <el-input-number v-model="formData.taskUnit.config.showRate" :min="1"></el-input-number>
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item label="领取次数限制">
                  <el-input-number v-model="formData.taskUnit.config.limit"></el-input-number>
                </el-form-item>
              </el-col>
            </el-row>
            
            <el-form-item label="判定文本">
              <el-input v-model="formData.taskUnit.config.checkText" placeholder="宣传文案任务需要使用"></el-input>
            </el-form-item>
            
            <el-form-item label="奖励Key">
              <reward-key-selector v-model="formData.taskUnit.config.awardsKey" placeholder="请选择奖励Key"></reward-key-selector>
            </el-form-item>
            
            <el-row :gutter="20">
              <el-col :span="12">
                <el-form-item label="自动发送通知">
                  <el-switch v-model="formData.taskUnit.config.autoSendNotice"></el-switch>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="通知消息">
                  <el-input v-model="formData.taskUnit.config.notice" placeholder="官方通知消息"></el-input>
                </el-form-item>
              </el-col>
            </el-row>
          </div>

          <!-- 多节点任务配置 -->
          <div class="task-configs-section" v-if="formData.taskUnit.mode === 2">
            <div class="section-header">
              <h4>多节点任务配置</h4>
              <div class="header-actions">
                <el-button size="small" type="primary" @click="addTaskConfig">添加节点配置</el-button>
                <el-button 
                  v-if="formData.taskUnit.configs.length > 0"
                  size="small" 
                  @click="toggleTaskConfigsCollapse">
                  {{ taskConfigsCollapsed ? '展开全部' : '收起全部' }}
                  <i :class="taskConfigsCollapsed ? 'el-icon-arrow-down' : 'el-icon-arrow-up'"></i>
                </el-button>
              </div>
            </div>
            
            <div class="task-configs-summary" v-if="formData.taskUnit.configs.length > 0">
              <el-alert 
                :title="`已配置 ${formData.taskUnit.configs.length} 个任务节点`" 
                type="info" 
                :closable="false"
                show-icon>
              </el-alert>
            </div>
            
            <el-collapse v-model="activeTaskConfigs" class="task-configs-collapse">
              <el-collapse-item 
                v-for="(config, index) in formData.taskUnit.configs" 
                :key="index" 
                :name="`task-${index}`"
                class="task-config-item"
              >
                <template slot="title">
                  <div class="collapse-title">
                    <span class="config-index">任务节点 {{ index + 1 }}</span>
                    <span class="config-preview">
                      {{ config.checkText || '未设置文本' }} - 
                      {{ config.awardsKey || '未设置奖励' }}
                    </span>
                    <el-button 
                      size="mini" 
                      type="danger" 
                      icon="el-icon-delete" 
                      @click.stop="removeTaskConfig(index)"
                      style="margin-left: auto;">
                      删除
                    </el-button>
                  </div>
                </template>
                
                <el-form :inline="false" label-width="100px" class="collapse-content">
                  <el-row :gutter="16">
                    <el-col :span="8">
                      <el-form-item label="判定值">
                        <el-input-number v-model="config.checkParams" :min="0" style="width: 100%"></el-input-number>
                      </el-form-item>
                    </el-col>
                    <el-col :span="8">
                      <el-form-item label="显示倍率">
                        <el-input-number v-model="config.showRate" :min="1" style="width: 100%"></el-input-number>
                      </el-form-item>
                    </el-col>
                    <el-col :span="8">
                      <el-form-item label="次数限制">
                        <el-input-number v-model="config.limit" style="width: 100%"></el-input-number>
                      </el-form-item>
                    </el-col>
                  </el-row>
                  
                  <el-row :gutter="16">
                    <el-col :span="12">
                      <el-form-item label="判定文本">
                        <el-input v-model="config.checkText" placeholder="宣传文案"></el-input>
                      </el-form-item>
                    </el-col>
                    <el-col :span="12">
                      <el-form-item label="奖励Key">
                        <reward-key-selector v-model="config.awardsKey" placeholder="请选择奖励Key"></reward-key-selector>
                      </el-form-item>
                    </el-col>
                  </el-row>
                  
                  <el-row :gutter="16">
                    <el-col :span="12">
                      <el-form-item label="自动通知">
                        <el-switch v-model="config.autoSendNotice"></el-switch>
                      </el-form-item>
                    </el-col>
                    <el-col :span="12">
                      <el-form-item label="通知消息">
                        <el-input v-model="config.notice" placeholder="官方通知消息"></el-input>
                      </el-form-item>
                    </el-col>
                  </el-row>
                </el-form>
              </el-collapse-item>
            </el-collapse>
            
            <!-- 空状态 -->
            <el-empty v-if="formData.taskUnit.configs.length === 0" description="暂无多节点配置" style="padding: 20px;">
              <el-button type="primary" @click="addTaskConfig">添加第一个节点配置</el-button>
            </el-empty>
          </div>
        </el-card>

        <!-- 排行组件配置 -->
        <el-card v-if="formData.unitType === 4" header="排行组件配置" class="form-section">
          <el-row :gutter="20">
            <el-col :span="8">
              <el-form-item label="榜单类型">
                <el-select v-model="formData.rankUnit.rankType" filterable placeholder="请选择榜单类型">
                  <el-option v-for="item in rankTypeConstants" :key="item.value" 
                    :label="item.name" :value="item.value">
                  </el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="显示TOP">
                <el-input-number v-model="formData.rankUnit.showTop" :min="1" :value="10"></el-input-number>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="奖励TOP">
                <el-input-number v-model="formData.rankUnit.awardTop" :min="1" :value="10"></el-input-number>
              </el-form-item>
            </el-col>
          </el-row>
          
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="积分显示倍率">
                <el-input-number v-model="formData.rankUnit.scoreRate" :min="1" :value="1"></el-input-number>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="积分图标">
                <div style="display: flex; align-items: center; gap: 10px;">
                  <UploadSingleImg v-model="formData.rankUnit.scoreIcon" 
                    :on-upload="uploadRankScoreIcon" 
                    upload-dir="/rank_score/unit/event/" />
                  <el-input v-model="formData.rankUnit.scoreIcon" placeholder="请输入积分图标URL" style="flex: 1;"></el-input>
                </div>
              </el-form-item>
            </el-col>
          </el-row>
          
          <!-- 榜单周期配置 -->
          <div class="rank-duration-config">
            <div class="section-header">
              <h4>榜单周期配置</h4>
            </div>
            
            <el-form-item label="榜单周期类型">
              <el-select v-model="formData.rankUnit.rankDuration" placeholder="请选择榜单周期类型">
                <el-option 
                  v-for="item in rankCycleTypeConstants" 
                  :key="item.value" 
                  :value="item.value" 
                  :label="item.name">
                </el-option>
              </el-select>
              <div style="font-size: 12px; color: #999; margin-top: 4px;">
                选择榜单的周期类型，影响榜单数据的统计和展示
              </div>
            </el-form-item>
          </div>

          <!-- 榜单时间配置 -->
          <div v-if="formData.rankUnit.rankDuration === 0">
            <el-form-item label="榜单时间设置">
              <el-radio-group v-model="useDefaultRankTime">
                <el-radio :label="true">使用活动默认时间</el-radio>
                <el-radio :label="false">自定义榜单时间</el-radio>
              </el-radio-group>
            </el-form-item>
            
            <el-row v-if="!useDefaultRankTime" :gutter="20">
              <el-col :span="12">
                <el-form-item label="榜单开始时间">
                  <el-date-picker 
                    v-model="rankStartTime" 
                    type="datetime" 
                    placeholder="选择榜单开始时间"
                    style="width: 100%"
                    value-format="timestamp">
                  </el-date-picker>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="榜单结束时间">
                  <el-date-picker 
                    v-model="rankEndTime" 
                    type="datetime" 
                    placeholder="选择榜单结束时间"
                    style="width: 100%"
                    value-format="timestamp">
                  </el-date-picker>
                </el-form-item>
              </el-col>
            </el-row>
          </div>

          <!-- 榜单时间显示（当榜单周期类型不为0时） -->
          <div v-if="formData.rankUnit.rankDuration !== 0">
            <el-row :gutter="20">
              <el-col :span="12">
                <el-form-item label="榜单开始时间">
                  <el-date-picker 
                    v-model="rankStartTime" 
                    type="datetime" 
                    placeholder="选择榜单开始时间"
                    style="width: 100%"
                    value-format="timestamp">
                  </el-date-picker>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="榜单结束时间">
                  <el-date-picker 
                    v-model="rankEndTime" 
                    type="datetime" 
                    placeholder="选择榜单结束时间"
                    style="width: 100%"
                    value-format="timestamp">
                  </el-date-picker>
                </el-form-item>
              </el-col>
            </el-row>
          </div>
          
          <el-form-item label="校验参数">
            <el-input v-model="rankCheckParamsStr" placeholder="请输入校验参数，多个用逗号分隔（字符串格式）"></el-input>
            <div style="font-size: 12px; color: #999; margin-top: 4px;">
              示例：1,2066,2069 或 param1,param2,param3
            </div>
          </el-form-item>
          
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="自动发放奖励">
                <el-switch v-model="formData.rankUnit.autoGiveAward"></el-switch>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="自动发送全局通知">
                <el-switch v-model="formData.rankUnit.autoSendGlobalNotice"></el-switch>
              </el-form-item>
            </el-col>
          </el-row>

          <!-- 官方消息支持渠道配置 -->
          <el-form-item label="官方消息支持渠道">
            <el-select 
              v-model="formData.rankUnit.noticeSupportChannel" 
              multiple 
              filterable 
              collapse-tags
              clearable
              placeholder="请选择支持发送官方消息的渠道（为空则不限制）" 
              style="width: 100%">
              <el-option 
                v-for="item in channelList" 
                :key="item.value" 
                :label="item.name || item.label" 
                :value="item.value">
              </el-option>
            </el-select>
            <div style="font-size: 12px; color: #999; margin-top: 4px;">
              选择支持发送官方通知消息的渠道列表，可多选
            </div>
          </el-form-item>

          <!-- 队伍榜单配置 -->
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="是否为队伍榜单">
                <el-switch v-model="formData.rankUnit.teamRank"></el-switch>
              </el-form-item>
            </el-col>
            <el-col :span="12" v-if="formData.rankUnit.teamRank">
              <el-form-item label="队伍组件ID" prop="teamUnitId">
                <el-input-number v-model="formData.rankUnit.teamUnitId" :min="1" placeholder="请输入队伍组件ID"></el-input-number>
              </el-form-item>
            </el-col>
          </el-row>

          <!-- 子榜单配置 -->
          <div class="son-rank-config">
            <div class="section-header">
              <h4>子榜单配置</h4>
            </div>
            
            <el-form-item label="开启子榜单">
              <el-switch 
                v-model="formData.rankUnit.enableSonRank"
                :disabled="formData.rankUnit.teamRank"
                @change="handleSonRankChange">
              </el-switch>
              <div style="font-size: 12px; color: #999; margin-top: 4px;">
                只有收礼相关榜单可以开启子榜单，其他类型榜单配置无效，队伍榜单无法开启子榜单
              </div>
              <div v-if="formData.rankUnit.teamRank" style="font-size: 12px; color: #f56c6c; margin-top: 4px;">
                队伍榜单无法开启子榜单
              </div>
            </el-form-item>

            <div v-if="formData.rankUnit.enableSonRank" class="son-rank-config-form">
              <el-row :gutter="20">
                <el-col :span="8">
                  <el-form-item label="展示的子榜单TOP数量">
                    <el-input-number v-model="formData.rankUnit.sonRankConfig.showTop" :min="1" style="width: 100%"></el-input-number>
                  </el-form-item>
                </el-col>
                <el-col :span="8">
                  <el-form-item label="奖励的子榜单TOP数量">
                    <el-input-number v-model="formData.rankUnit.sonRankConfig.awardTop" :min="1" style="width: 100%"></el-input-number>
                  </el-form-item>
                </el-col>
                <el-col :span="8">
                  <el-form-item label="限制只能异性上榜">
                    <el-switch v-model="formData.rankUnit.sonRankConfig.diffSex"></el-switch>
                    <div style="font-size: 12px; color: #999; margin-top: 4px;">
                      开启后，子榜成员只能异性上榜
                    </div>
                  </el-form-item>
                </el-col>
              </el-row>
            </div>
          </div>

          <!-- 实时活动榜单标签配置 -->
          <div class="rank-label-config">
            <div class="section-header">
              <h4>实时活动榜单标签配置</h4>
            </div>
            
            <el-form-item label="开启实时榜单标签">
              <el-switch 
                v-model="formData.rankUnit.enableRankLabel"
                @change="handleRankLabelChange">
              </el-switch>
              <div style="font-size: 12px; color: #999; margin-top: 4px;">
                开启后可在榜单中显示实时活动标签
              </div>
            </el-form-item>

            <div v-if="formData.rankUnit.enableRankLabel" class="rank-label-configs">
              <!-- 调试信息 -->
              <div style="background: #f0f0f0; padding: 5px; margin-bottom: 10px; font-size: 12px; color: #666;">
                调试: enableRankLabel = {{ formData.rankUnit.enableRankLabel }}, 
                标签配置数量 = {{ (formData.rankUnit.rankLabelConfigs || []).length }}
              </div>
              <div class="section-header">
                <h5>标签配置列表</h5>
                <div class="header-actions">
                  <el-button size="small" type="primary" @click="addRankLabelConfig">添加标签配置</el-button>
                  <el-button 
                    v-if="(formData.rankUnit.rankLabelConfigs || []).length > 0"
                    size="small" 
                    @click="toggleRankLabelsCollapse">
                    {{ rankLabelsCollapsed ? '展开全部' : '收起全部' }}
                    <i :class="rankLabelsCollapsed ? 'el-icon-arrow-down' : 'el-icon-arrow-up'"></i>
                  </el-button>
                </div>
              </div>
              
              <div class="rank-labels-summary" v-if="(formData.rankUnit.rankLabelConfigs || []).length > 0">
                <el-alert 
                  :title="`已配置 ${(formData.rankUnit.rankLabelConfigs || []).length} 个标签配置`" 
                  type="info" 
                  :closable="false"
                  show-icon>
                </el-alert>
              </div>
              
              <el-collapse v-model="activeRankLabels" class="rank-labels-collapse">
                <el-collapse-item 
                  v-for="(label, index) in (formData.rankUnit.rankLabelConfigs || [])" 
                  :key="index" 
                  :name="`label-${index}`"
                  class="rank-label-item"
                >
                  <template slot="title">
                    <div class="collapse-title">
                      <span class="config-index">标签配置 {{ index + 1 }}</span>
                      <span class="config-preview">
                        前{{ label.maxTop || '?' }}名 - 权重: {{ label.weight || '未设置' }}
                      </span>
                      <el-button 
                        size="mini" 
                        type="danger" 
                        icon="el-icon-delete" 
                        @click.stop="removeRankLabelConfig(index)"
                        style="margin-left: auto;">
                        删除
                      </el-button>
                    </div>
                  </template>
                  
                  <el-form :inline="false" label-width="120px" class="collapse-content">
                    <el-row :gutter="16">
                      <el-col :span="12">
                        <el-form-item label="最大榜单名次">
                          <el-input-number v-model="label.maxTop" :min="1" style="width: 100%" placeholder="如：前10名"></el-input-number>
                        </el-form-item>
                      </el-col>
                      <el-col :span="12">
                        <el-form-item label="权重">
                          <el-input-number v-model="label.weight" :min="0" style="width: 100%" placeholder="权重（越小越靠前）"></el-input-number>
                        </el-form-item>
                      </el-col>
                    </el-row>
                    
                    <el-form-item label="标签图片">
                      <div style="display: flex; align-items: center; gap: 10px;">
                        <UploadSingleImg v-model="label.labelImg" 
                          :on-upload="uploadRankLabelImg" 
                          upload-dir="/label/rank/unit_event/" />
                        <el-input v-model="label.labelImg" placeholder="请输入标签图片URL" style="flex: 1;"></el-input>
                      </div>
                    </el-form-item>
                    
                    <el-form-item label="标签说明">
                      <el-tag :type="label.labelImg ? 'success' : 'info'">
                        前{{ label.maxTop || '?' }}名 - 权重: {{ label.weight || '未设置' }} - {{ label.labelImg || '未设置图片' }}
                      </el-tag>
                    </el-form-item>
                  </el-form>
                </el-collapse-item>
              </el-collapse>
              
              <!-- 空状态 -->
              <el-empty v-if="(formData.rankUnit.rankLabelConfigs || []).length === 0" description="暂无标签配置" style="padding: 20px;">
                <el-button type="primary" @click="addRankLabelConfig">添加第一个标签配置</el-button>
              </el-empty>
            </div>
          </div>

          <!-- 榜单奖池配置 -->
          <div class="rank-pool-config">
            <div class="section-header">
              <h4>榜单奖池配置</h4>
            </div>
            
            <el-form-item label="开启榜单总数据分成奖池">
              <el-switch 
                v-model="formData.rankUnit.enablePool"
                @change="handlePoolChange">
              </el-switch>
              <div style="font-size: 12px; color: #999; margin-top: 4px;">
                开启后榜单数据将参与奖池分成
              </div>
            </el-form-item>

            <div v-if="formData.rankUnit.enablePool" class="rank-pool-info">
              <!-- 调试信息 -->
              <div style="background: #f0f0f0; padding: 5px; margin-bottom: 10px; font-size: 12px; color: #666;">
                调试: enablePool = {{ formData.rankUnit.enablePool }}, 
                分成配置数量 = {{ (formData.rankUnit.rankPoolInfo.rankPoolConfigs || []).length }}
              </div>
              <el-row :gutter="20">
                <el-col :span="12">
                  <el-form-item label="榜单数值万分率">
                    <div style="display: flex; align-items: center;">
                      <el-input-number 
                        v-model="formData.rankUnit.rankPoolInfo.poolRate" 
                        :min="0" 
                        placeholder="万分率，如：10000表示100%"
                        style="flex: 1;">
                      </el-input-number>
                      <el-tooltip content="10000=100%；5000=50%；1000=10%；100=1%" placement="top">
                        <i class="el-icon-question" style="margin-left: 8px; color: #909399; cursor: pointer;"></i>
                      </el-tooltip>
                    </div>
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="奖池金额展示倍率">
                    <el-input-number 
                      v-model="formData.rankUnit.rankPoolInfo.showRate" 
                      :min="0" 
                      placeholder="奖池金额展示倍率">
                    </el-input-number>
                  </el-form-item>
                </el-col>
              </el-row>
              
              <el-row :gutter="20">
                <el-col :span="12">
                  <el-form-item label="基础奖池金额">
                    <el-input 
                      v-model="formData.rankUnit.rankPoolInfo.basePoolAmount" 
                      placeholder="基础奖池金额(后台金币）">
                    </el-input>
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="奖池金额上限">
                    <el-input 
                      v-model="formData.rankUnit.rankPoolInfo.maxPoolAmount" 
                      placeholder="奖池金额上限(后台金币）">
                    </el-input>
                  </el-form-item>
                </el-col>
              </el-row>

              <!-- 榜单奖池分成配置 -->
              <div class="rank-pool-configs">
                <div class="section-header">
                  <h5>榜单奖池分成配置</h5>
                  <div class="header-actions">
                    <el-button size="small" type="primary" @click="addRankPoolConfig">添加分成配置</el-button>
                    <el-button 
                      v-if="(formData.rankUnit.rankPoolInfo.rankPoolConfigs || []).length > 0"
                      size="small" 
                      @click="toggleRankPoolConfigsCollapse">
                      {{ rankPoolConfigsCollapsed ? '展开全部' : '收起全部' }}
                      <i :class="rankPoolConfigsCollapsed ? 'el-icon-arrow-down' : 'el-icon-arrow-up'"></i>
                    </el-button>
                  </div>
                </div>
                
                <div class="rank-pool-configs-summary" v-if="(formData.rankUnit.rankPoolInfo.rankPoolConfigs || []).length > 0">
                  <el-alert 
                    :title="`已配置 ${(formData.rankUnit.rankPoolInfo.rankPoolConfigs || []).length} 个分成配置`" 
                    type="warning" 
                    :closable="false"
                    show-icon>
                  </el-alert>
                </div>
                
                <el-collapse v-model="activeRankPoolConfigs" class="rank-pool-configs-collapse">
                  <el-collapse-item 
                    v-for="(config, index) in (formData.rankUnit.rankPoolInfo.rankPoolConfigs || [])" 
                    :key="index" 
                    :name="`poolconfig-${index}`"
                    class="rank-pool-config-item"
                  >
                    <template slot="title">
                      <div class="collapse-title">
                        <span class="config-index">分成配置 {{ index + 1 }}</span>
                        <span class="config-preview">
                          {{ formatRankPoolRange(config, index) }} - 万分率: {{ config.rate || '未设置' }}
                        </span>
                        <el-button 
                          size="mini" 
                          type="danger" 
                          icon="el-icon-delete" 
                          @click.stop="removeRankPoolConfig(index)"
                          style="margin-left: auto;">
                          删除
                        </el-button>
                      </div>
                    </template>
                    
                    <el-form :inline="false" label-width="120px" class="collapse-content">
                      <el-row :gutter="16">
                        <el-col :span="8">
                          <el-form-item label="最小榜单名次">
                            <el-input-number v-model="config.minTop" :min="1" style="width: 100%" placeholder="如：第1名"></el-input-number>
                          </el-form-item>
                        </el-col>
                        <el-col :span="8">
                          <el-form-item label="最小积分数">
                            <el-input-number v-model="config.minScore" :min="0" style="width: 100%" placeholder="最小积分数"></el-input-number>
                          </el-form-item>
                        </el-col>
                        <el-col :span="8">
                          <el-form-item label="万分率">
                            <el-input-number v-model="config.rate" :min="0" style="width: 100%" placeholder="万分率"></el-input-number>
                          </el-form-item>
                        </el-col>
                      </el-row>
                      
                      <el-form-item label="分成说明">
                        <el-tag :type="config.rate ? 'success' : 'info'">
                          第{{ config.minTop || '?' }}名 - 积分≥{{ config.minScore || '0' }} - 分成率: {{ config.rate / 100 || '未设置' }}%
                        </el-tag>
                      </el-form-item>
                    </el-form>
                  </el-collapse-item>
                </el-collapse>
                
                <!-- 空状态 -->
                <el-empty v-if="(formData.rankUnit.rankPoolInfo.rankPoolConfigs || []).length === 0" description="暂无分成配置" style="padding: 20px;">
                  <el-button type="primary" @click="addRankPoolConfig">添加第一个分成配置</el-button>
                </el-empty>
              </div>
            </div>
          </div>

          <!-- 榜单奖励配置 -->
          <div class="rank-rewards-section">
            <div class="section-header">
              <h4>榜单奖励配置</h4>
              <div class="header-actions">
                <el-button size="small" type="primary" @click="addRankReward">添加奖励配置</el-button>
                <el-button 
                  v-if="formData.rankUnit.rewardConfigs.length > 0"
                  size="small" 
                  @click="toggleRankRewardsCollapse">
                  {{ rankRewardsCollapsed ? '展开全部' : '收起全部' }}
                  <i :class="rankRewardsCollapsed ? 'el-icon-arrow-down' : 'el-icon-arrow-up'"></i>
                </el-button>
              </div>
            </div>
            
            <div class="rank-rewards-summary" v-if="formData.rankUnit.rewardConfigs.length > 0">
              <el-alert 
                :title="`已配置 ${formData.rankUnit.rewardConfigs.length} 个奖励配置`" 
                type="success" 
                :closable="false"
                show-icon>
              </el-alert>
            </div>
            
            <el-collapse v-model="activeRankRewards" class="rank-rewards-collapse">
              <el-collapse-item 
                v-for="(reward, index) in formData.rankUnit.rewardConfigs" 
                :key="index" 
                :name="`rank-${index}`"
                class="rank-reward-item"
              >
                <template slot="title">
                  <div class="collapse-title">
                    <span class="config-index">奖励配置 {{ index + 1 }}</span>
                    <span class="config-preview">
                      {{ formatRankRewardRange(reward, index) }} - {{ reward.awardsKey || '未设置奖励' }}
                    </span>
                    <el-button 
                      size="mini" 
                      type="danger" 
                      icon="el-icon-delete" 
                      @click.stop="removeRankReward(index)"
                      style="margin-left: auto;">
                      删除
                    </el-button>
                  </div>
                </template>
                
                <el-form :inline="false" label-width="120px" class="collapse-content">
                  <el-row :gutter="16">
                    <el-col :span="12">
                      <el-form-item label="最小排名">
                        <el-input-number v-model="reward.minTop" :min="1" style="width: 100%" placeholder="如：第1名"></el-input-number>
                      </el-form-item>
                    </el-col>
                    <el-col :span="12">
                      <el-form-item label="奖励Key">
                        <reward-key-selector v-model="reward.awardsKey" placeholder="请选择奖励Key"></reward-key-selector>
                      </el-form-item>
                    </el-col>
                  </el-row>
                  
                  <el-row :gutter="16">
                    <el-col :span="12">
                      <el-form-item label="子榜单奖励Key">
                        <reward-key-selector v-model="reward.sonRankAwardsKey" placeholder="请选择子榜单奖励Key"></reward-key-selector>
                        <div style="font-size: 12px; color: #999; margin-top: 4px;">
                          子榜单的奖励配置，开启子榜单时生效
                        </div>
                      </el-form-item>
                    </el-col>
                  </el-row>
                  
                  <el-form-item label="奖励说明">
                    <el-tag :type="reward.awardsKey ? 'success' : 'info'">
                      {{ formatRankRewardRange(reward, index) }} - {{ reward.awardsKey || '未选择奖励' }}
                    </el-tag>
                    <el-tag v-if="reward.sonRankAwardsKey" type="warning" style="margin-left: 8px;">
                      子榜单: {{ reward.sonRankAwardsKey }}
                    </el-tag>
                  </el-form-item>
                </el-form>
              </el-collapse-item>
            </el-collapse>
            
            <!-- 空状态 -->
            <el-empty v-if="formData.rankUnit.rewardConfigs.length === 0" description="暂无奖励配置" style="padding: 20px;">
              <el-button type="primary" @click="addRankReward">添加第一个奖励配置</el-button>
            </el-empty>
          </div>
        </el-card>

        <!-- 步频组件配置 -->
        <el-card v-if="formData.unitType === 5" header="步频组件配置" class="form-section">
          <el-form-item label="消耗货币组件">
            <CurrencyUnitSelector 
              v-model="formData.stepPoolUnit.costUnitIds" 
              :all-units="allUnits"
              :multiple="true"
              placeholder="请选择消耗的货币组件">
            </CurrencyUnitSelector>
          </el-form-item>
          
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="最大步频">
                <el-input-number v-model="formData.stepPoolUnit.maxSteps" :min="1" placeholder="最大步频数量"></el-input-number>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="刷新消耗">
                <el-input-number v-model="formData.stepPoolUnit.refreshCost" :min="0" placeholder="重置步频的消耗"></el-input-number>
              </el-form-item>
            </el-col>
          </el-row>

          <!-- 子奖池配置 -->
          <div class="step-sonpools-config">
            <div class="section-header">
              <h4>子奖池配置</h4>
              <div class="header-actions">
                <el-button size="small" type="primary" @click="addSonPool">添加子奖池</el-button>
                <el-button 
                  v-if="formData.stepPoolUnit.sonPools.length > 0"
                  size="small" 
                  @click="toggleSonPoolsCollapse">
                  {{ sonPoolsCollapsed ? '展开全部' : '收起全部' }}
                  <i :class="sonPoolsCollapsed ? 'el-icon-arrow-down' : 'el-icon-arrow-up'"></i>
                </el-button>
              </div>
            </div>
            
            <div class="sonpools-summary" v-if="formData.stepPoolUnit.sonPools.length > 0">
              <el-alert 
                :title="`已配置 ${formData.stepPoolUnit.sonPools.length} 个子奖池`" 
                type="info" 
                :closable="false"
                show-icon>
              </el-alert>
            </div>
            
            <el-collapse v-model="activeSonPools" class="sonpools-collapse">
              <el-collapse-item 
                v-for="(pool, index) in formData.stepPoolUnit.sonPools" 
                :key="index" 
                :name="`pool-${index}`"
                class="sonpool-item"
              >
                <template slot="title">
                  <div class="collapse-title">
                    <span class="config-index">子奖池 {{ index + 1 }}</span>
                    <span class="config-preview">
                      {{ pool.poolName || '未命名' }} - 
                      {{ pool.poolId || '未设置ID' }}
                    </span>
                    <el-button 
                      size="mini" 
                      type="danger" 
                      icon="el-icon-delete" 
                      @click.stop="removeSonPool(index)"
                      style="margin-left: auto;">
                      删除
                    </el-button>
                  </div>
                </template>
                
                <el-form :inline="false" label-width="100px" class="collapse-content">
                  <el-row :gutter="16">
                    <el-col :span="12">
                      <el-form-item label="子奖池ID">
                        <el-input v-model="pool.poolId" placeholder="子奖池id"></el-input>
                      </el-form-item>
                    </el-col>
                    <el-col :span="12">
                      <el-form-item label="奖池名称">
                        <el-input v-model="pool.poolName" placeholder="子奖池名称"></el-input>
                      </el-form-item>
                    </el-col>
                  </el-row>
                  
                  <el-form-item label="奖池资源Key">
                    <reward-key-selector v-model="pool.awardsKey" placeholder="请选择奖励Key"></reward-key-selector>
                  </el-form-item>
                </el-form>
              </el-collapse-item>
            </el-collapse>
            
            <!-- 空状态 -->
            <el-empty v-if="formData.stepPoolUnit.sonPools.length === 0" description="暂无子奖池配置" style="padding: 20px;">
              <el-button type="primary" @click="addSonPool">添加第一个子奖池</el-button>
            </el-empty>
          </div>

          <!-- 步频配置 -->
          <div class="step-configs-config">
            <div class="section-header">
              <h4>步频配置</h4>
              <div class="header-actions">
                <el-button size="small" type="primary" @click="addStepConfig">添加步频配置</el-button>
                <el-button 
                  v-if="formData.stepPoolUnit.stepConfigs.length > 0"
                  size="small" 
                  @click="toggleStepConfigsCollapse">
                  {{ stepConfigsCollapsed ? '展开全部' : '收起全部' }}
                  <i :class="stepConfigsCollapsed ? 'el-icon-arrow-down' : 'el-icon-arrow-up'"></i>
                </el-button>
              </div>
            </div>
            
            <div class="stepconfigs-summary" v-if="formData.stepPoolUnit.stepConfigs.length > 0">
              <el-alert 
                :title="`已配置 ${formData.stepPoolUnit.stepConfigs.length} 个步频配置`" 
                type="warning" 
                :closable="false"
                show-icon>
              </el-alert>
            </div>
            
            <el-collapse v-model="activeStepConfigs" class="stepconfigs-collapse">
              <el-collapse-item 
                v-for="(config, index) in formData.stepPoolUnit.stepConfigs" 
                :key="index" 
                :name="`stepconfig-${index}`"
                class="stepconfig-item"
              >
                <template slot="title">
                  <div class="collapse-title">
                    <span class="config-index">步频 {{ config.step || (index + 1) }}</span>
                    <span class="config-preview">
                      消耗: {{ config.cost || 0 }} - 
                      剩余: {{ config.remainingCost || 0 }}
                    </span>
                    <el-button 
                      size="mini" 
                      type="danger" 
                      icon="el-icon-delete" 
                      @click.stop="removeStepConfig(index)"
                      style="margin-left: auto;">
                      删除
                    </el-button>
                  </div>
                </template>
                
                <el-form :inline="false" label-width="120px" class="collapse-content">
                  <el-row :gutter="16">
                    <el-col :span="8">
                      <el-form-item label="步频序号">
                        <el-input-number v-model="config.step" :min="1" style="width: 100%"></el-input-number>
                      </el-form-item>
                    </el-col>
                    <el-col :span="8">
                      <el-form-item label="消耗数量">
                        <el-input-number v-model="config.cost" :min="0" style="width: 100%"></el-input-number>
                      </el-form-item>
                    </el-col>
                    <el-col :span="8">
                      <el-form-item label="剩余消耗">
                        <el-input-number v-model="config.remainingCost" :min="0" style="width: 100%"></el-input-number>
                      </el-form-item>
                    </el-col>
                  </el-row>
                  
                  <el-row :gutter="16">
                    <el-col :span="12">
                      <el-form-item label="子奖池ID">
                        <el-input v-model="config.poolId" placeholder="对应子奖池ID"></el-input>
                      </el-form-item>
                    </el-col>
                  </el-row>
                </el-form>
              </el-collapse-item>
            </el-collapse>
            
            <!-- 空状态 -->
            <el-empty v-if="formData.stepPoolUnit.stepConfigs.length === 0" description="暂无步频配置" style="padding: 20px;">
              <el-button type="primary" @click="addStepConfig">添加第一个步频配置</el-button>
            </el-empty>
          </div>

          <!-- 指定步频奖励配置 -->
          <div class="step-rewards-config">
            <div class="section-header">
              <h4>指定步频奖励配置</h4>
              <div class="header-actions">
                <el-button size="small" type="primary" @click="addStepReward">添加步频奖励</el-button>
                <el-button 
                  v-if="formData.stepPoolUnit.stepRewards.length > 0"
                  size="small" 
                  @click="toggleStepRewardsCollapse">
                  {{ stepRewardsCollapsed ? '展开全部' : '收起全部' }}
                  <i :class="stepRewardsCollapsed ? 'el-icon-arrow-down' : 'el-icon-arrow-up'"></i>
                </el-button>
              </div>
            </div>
            
            <div class="steprewards-summary" v-if="formData.stepPoolUnit.stepRewards.length > 0">
              <el-alert 
                :title="`已配置 ${formData.stepPoolUnit.stepRewards.length} 个步频奖励`" 
                type="success" 
                :closable="false"
                show-icon>
              </el-alert>
            </div>
            
            <el-collapse v-model="activeStepRewards" class="steprewards-collapse">
              <el-collapse-item 
                v-for="(reward, index) in formData.stepPoolUnit.stepRewards" 
                :key="index" 
                :name="`stepreward-${index}`"
                class="stepreward-item"
              >
                <template slot="title">
                  <div class="collapse-title">
                    <span class="config-index">步频奖励 {{ index + 1 }}</span>
                    <span class="config-preview">
                      步频{{ reward.step || '未设置' }} - 
                      {{ reward.awardsKey || '未设置奖励' }}
                    </span>
                    <el-button 
                      size="mini" 
                      type="danger" 
                      icon="el-icon-delete" 
                      @click.stop="removeStepReward(index)"
                      style="margin-left: auto;">
                      删除
                    </el-button>
                  </div>
                </template>
                
                <el-form :inline="false" label-width="100px" class="collapse-content">
                  <el-row :gutter="16">
                    <el-col :span="12">
                      <el-form-item label="触发步频">
                        <el-input-number v-model="reward.step" :min="1" style="width: 100%"></el-input-number>
                      </el-form-item>
                    </el-col>
                    <el-col :span="12">
                      <el-form-item label="奖励Key">
                        <reward-key-selector v-model="reward.awardsKey" placeholder="请选择奖励Key"></reward-key-selector>
                      </el-form-item>
                    </el-col>
                  </el-row>
                  
                  <el-form-item label="奖励通知消息">
                    <el-input v-model="reward.notice" placeholder="奖励通知消息"></el-input>
                  </el-form-item>
                </el-form>
              </el-collapse-item>
            </el-collapse>
            
            <!-- 空状态 -->
            <el-empty v-if="formData.stepPoolUnit.stepRewards.length === 0" description="暂无步频奖励配置" style="padding: 20px;">
              <el-button type="primary" @click="addStepReward">添加第一个步频奖励</el-button>
            </el-empty>
          </div>
        </el-card>

        <!-- 商店组件配置 -->
        <el-card v-if="formData.unitType === 6" header="商店组件配置" class="form-section">
          <el-form-item label="消耗货币组件">
            <CurrencyUnitSelector 
              v-model="formData.shopUnit.costUnitIds" 
              :all-units="allUnits"
              :multiple="true"
              placeholder="请选择消耗的货币组件">
            </CurrencyUnitSelector>
          </el-form-item>
          
          <el-form-item label="限制条件使用周期" prop="shopDuration">
            <el-select v-model="formData.shopUnit.duration" placeholder="请选择周期" style="width: 100%">
              <el-option v-for="item in taskDurationConstants" :key="item.value" 
                :label="item.name" :value="item.value">
              </el-option>
            </el-select>
            <div style="font-size: 12px; color: #999; margin-top: 4px;">
              设置商品限购的周期类型
            </div>
          </el-form-item>
          
          <div class="shop-items-config">
            <div class="section-header">
              <h4>商品配置</h4>
              <div class="header-actions">
                <el-button size="small" type="primary" @click="addShopItem">添加商品</el-button>
                <el-button 
                  v-if="formData.shopUnit.items.length > 0"
                  size="small" 
                  @click="toggleShopItemsCollapse">
                  {{ shopItemsCollapsed ? '展开全部' : '收起全部' }}
                  <i :class="shopItemsCollapsed ? 'el-icon-arrow-down' : 'el-icon-arrow-up'"></i>
                </el-button>
              </div>
            </div>
            
            <div class="shop-items-summary" v-if="formData.shopUnit.items.length > 0">
              <el-alert 
                :title="`已配置 ${formData.shopUnit.items.length} 个商品`" 
                type="warning" 
                :closable="false"
                show-icon>
              </el-alert>
            </div>
            
            <el-collapse v-model="activeShopItems" class="shop-items-collapse">
              <el-collapse-item 
                v-for="(item, index) in formData.shopUnit.items" 
                :key="index" 
                :name="`shop-${index}`"
                class="shop-item"
              >
                <template slot="title">
                  <div class="collapse-title">
                    <span class="config-index">商品 {{ index + 1 }}</span>
                    <span class="config-preview">
                      {{ item.awardsKey || '未设置奖励' }} - {{ item.price || '未设置价格' }}
                    </span>
                    <el-button 
                      size="mini" 
                      type="danger" 
                      icon="el-icon-delete" 
                      @click.stop="removeShopItem(index)"
                      style="margin-left: auto;">
                      删除
                    </el-button>
                  </div>
                </template>
                
                <el-form :inline="false" label-width="100px" class="collapse-content">
                  <el-row :gutter="16">
                    <el-col :span="24">
                      <el-form-item label="奖励Key">
                        <reward-key-selector v-model="item.awardsKey" placeholder="请选择奖励Key"></reward-key-selector>
                      </el-form-item>
                    </el-col>
                  </el-row>
                  
                  <el-row :gutter="16">
                    <el-col :span="12">
                      <el-form-item label="显示价格">
                        <el-input v-model="item.showPrice" placeholder="如: 10000coins">
                          <template slot="prepend">显示</template>
                        </el-input>
                      </el-form-item>
                    </el-col>
                    <el-col :span="12">
                      <el-form-item label="实际价格">
                        <el-input-number 
                          v-model="item.price" 
                          :min="0" 
                          controls-position="right"
                          style="width: 100%">
                        </el-input-number>
                      </el-form-item>
                    </el-col>
                  </el-row>
                  
                  <el-row :gutter="16">
                    <el-col :span="8">
                      <el-form-item label="排序权重">
                        <el-input-number 
                          v-model="item.weight" 
                          :min="0" 
                          controls-position="right"
                          style="width: 100%">
                        </el-input-number>
                      </el-form-item>
                    </el-col>
                    <el-col :span="8">
                      <el-form-item label="全局限购数量">
                        <el-input-number 
                          v-model="item.globalLimit" 
                          :min="-1" 
                          controls-position="right"
                          style="width: 100%">
                        </el-input-number>
                        <div style="font-size: 12px; color: #999; margin-top: 4px;">
                          -1不限制，0已售罄，>0限制数量
                        </div>
                      </el-form-item>
                    </el-col>
                    <el-col :span="8">
                      <el-form-item label="商品状态">
                        <el-tag :type="item.awardsKey ? 'success' : 'info'">
                          {{ item.awardsKey ? '已配置' : '待配置' }}
                        </el-tag>
                      </el-form-item>
                    </el-col>
                  </el-row>
                </el-form>
              </el-collapse-item>
            </el-collapse>
            
            <!-- 空状态 -->
            <el-empty v-if="formData.shopUnit.items.length === 0" description="暂无商品配置" style="padding: 20px;">
              <el-button type="primary" @click="addShopItem">添加第一个商品</el-button>
            </el-empty>
          </div>
        </el-card>

        <!-- 货币合成组件配置 -->
        <el-card v-if="formData.unitType === 7" header="货币合成组件配置" class="form-section">
          <el-form-item label="消耗的货币组件">
            <CurrencyUnitSelector 
              v-model="formData.currencyMergeUnit.costCurrencyUnitIds" 
              :all-units="allUnits"
              :multiple="true"
              placeholder="请选择消耗的货币组件">
            </CurrencyUnitSelector>
          </el-form-item>
          
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="周期" prop="mergeDuration">
                <el-select v-model="formData.currencyMergeUnit.duration" placeholder="请选择周期" style="width: 100%">
                  <el-option v-for="item in taskDurationConstants" :key="item.value" 
                    :label="item.name" :value="item.value">
                  </el-option>
                </el-select>
                <div style="font-size: 12px; color: #999; margin-top: 4px;">
                  必填：合成限制的周期类型
                </div>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="周期内最大合成次数" prop="mergeLimit">
                <el-input-number v-model="formData.currencyMergeUnit.limit" :min="-1" 
                  placeholder="周期内每人最大可合成次数" style="width: 100%">
                </el-input-number>
                <div style="font-size: 12px; color: #999; margin-top: 4px;">
                  必填：-1不限制，0已售罄，>0限制数量
                </div>
              </el-form-item>
            </el-col>
          </el-row>
          
          <el-form-item label="奖励Key">
            <reward-key-selector v-model="formData.currencyMergeUnit.awardsKey" placeholder="请选择奖励Key"></reward-key-selector>
          </el-form-item>
        </el-card>

        <!-- 队伍组件配置 -->
        <el-card v-if="formData.unitType === 8" header="队伍组件配置" class="form-section">
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="最大人数" prop="maxMember">
                <el-input-number v-model="formData.teamUnit.maxMember" :min="1" :max="100" placeholder="队伍最大人数（包括队长）"></el-input-number>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="队伍周期" prop="duration">
                <el-select v-model="formData.teamUnit.duration" filterable placeholder="请选择队伍周期">
                  <el-option v-for="item in teamDurationConstants" :key="item.value" 
                    :label="item.name" :value="item.value">
                  </el-option>
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
          
          <el-form-item label="入队通知消息">
            <el-input v-model="formData.teamUnit.memberNotice" type="textarea" :rows="2" placeholder="新队员入队时发送给队员的官方消息"></el-input>
          </el-form-item>
          
          <el-form-item label="队长通知消息">
            <el-input v-model="formData.teamUnit.captainNotice" type="textarea" :rows="2" placeholder="有新队员入队时发送给队长的官方消息"></el-input>
          </el-form-item>
          
          <div style="font-size: 12px; color: #999; margin-top: 8px;">
            <p>• 最大人数：队伍的最大人数限制，包括队长在内</p>
            <p>• 队伍周期：队伍的有效期，到期后队伍将解散</p>
            <p>• 入队通知消息：新队员入队时发送给队员的官方消息</p>
            <p>• 队长通知消息：有新队员入队时发送给队长的官方消息</p>
          </div>
        </el-card>

        <!-- 分享奖励组件配置 -->
        <el-card v-if="formData.unitType === 9" header="分享奖励组件配置" class="form-section">
          <el-form-item label="消耗货币组件">
            <CurrencyUnitSelector 
              v-model="formData.shareUnit.costUnitIds" 
              :all-units="allUnits"
              :multiple="true"
              placeholder="请选择消耗的货币组件">
            </CurrencyUnitSelector>
          </el-form-item>
          
          <el-form-item label="分享一次需要消耗的货币数量" prop="needCost">
            <el-input-number v-model="formData.shareUnit.needCost" :min="0" 
              placeholder="分享一次需要消耗的货币数量" style="width: 100%"></el-input-number>
            <div style="font-size: 12px; color: #999; margin-top: 4px;">
              用户分享一次需要消耗的货币数量
            </div>
          </el-form-item>
          
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="分享者获得的奖励Key">
                <reward-key-selector v-model="formData.shareUnit.shareAwardsKey" placeholder="请选择分享者奖励Key"></reward-key-selector>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="被分享者获得的奖励Key">
                <reward-key-selector v-model="formData.shareUnit.shareToAwardsKey" placeholder="请选择被分享者奖励Key"></reward-key-selector>
              </el-form-item>
            </el-col>
          </el-row>
          
          <el-form-item label="被分享者接收的官方通知文案">
            <el-input v-model="formData.shareUnit.shareToNotice" type="textarea" :rows="3" 
              placeholder="被分享者接收到分享时显示的官方通知消息"></el-input>
            <div style="font-size: 12px; color: #999; margin-top: 4px;">
              此消息将发送给被分享的用户
            </div>
          </el-form-item>
          
          <div style="font-size: 12px; color: #999; margin-top: 8px;">
            <p>• 消耗货币组件：用户分享时需要消耗的货币类型</p>
            <p>• 分享一次消耗数量：每次分享需要消耗的货币数量</p>
            <p>• 分享者奖励：分享者完成分享后获得的奖励</p>
            <p>• 被分享者奖励：接受分享的用户获得的奖励</p>
            <p>• 官方通知文案：被分享者收到分享时的通知消息</p>
          </div>
        </el-card>

        <!-- 刮刮乐组件配置 -->
        <el-card v-if="formData.unitType === 10" header="刮刮乐组件配置" class="form-section">
          <el-form-item label="奖池组件ID" prop="poolUnitId">
            <el-input-number v-model="formData.scratchCardUnit.poolUnitId" :min="1" 
              placeholder="请输入奖池组件ID" style="width: 100%"></el-input-number>
            <div style="font-size: 12px; color: #999; margin-top: 4px;">
              刮刮乐使用的奖池组件ID
            </div>
          </el-form-item>

          <el-form-item label="其他数字奖池组件ID" prop="lowPoolUnitId">
            <el-input-number v-model="formData.scratchCardUnit.lowPoolUnitId" :min="1" 
              placeholder="请输入其他数字奖池组件ID" style="width: 100%"></el-input-number>
            <div style="font-size: 12px; color: #999; margin-top: 4px;">
              其他数字奖池组件ID
            </div>
          </el-form-item>

          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="其他数字随机数最小值" prop="randomMin">
                <el-input-number v-model="formData.scratchCardUnit.randomMin" :min="0" 
                  placeholder="最小值" style="width: 100%"></el-input-number>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="其他数字随机数最大值" prop="randomMax">
                <el-input-number v-model="formData.scratchCardUnit.randomMax" :min="0" 
                  placeholder="最大值" style="width: 100%"></el-input-number>
              </el-form-item>
            </el-col>
          </el-row>

          <!-- 奖励映射列表 -->
          <div class="mapping-list-section">
            <div class="section-header">
              <h4>奖励映射列表</h4>
              <div class="header-actions">
                <el-button size="small" type="primary" @click="addScratchCardMapping">添加映射</el-button>
                <el-button 
                  v-if="formData.scratchCardUnit.mappingList.length > 0"
                  size="small" 
                  @click="toggleScratchCardMappingsCollapse">
                  {{ scratchCardMappingsCollapsed ? '展开全部' : '收起全部' }}
                  <i :class="scratchCardMappingsCollapsed ? 'el-icon-arrow-down' : 'el-icon-arrow-up'"></i>
                </el-button>
              </div>
            </div>
            
            <div class="mapping-list-summary" v-if="formData.scratchCardUnit.mappingList.length > 0">
              <el-alert 
                :title="`已配置 ${formData.scratchCardUnit.mappingList.length} 个奖励映射`" 
                type="info" 
                :closable="false"
                show-icon>
              </el-alert>
            </div>
            
            <el-collapse v-model="activeScratchCardMappings" class="mapping-list-collapse">
              <el-collapse-item 
                v-for="(mapping, index) in formData.scratchCardUnit.mappingList" 
                :key="index" 
                :name="`mapping-${index}`"
                class="mapping-item"
              >
                <template slot="title">
                  <div class="collapse-title">
                    <span class="config-index">映射 {{ index + 1 }}</span>
                    <span class="config-preview">
                      {{ mapping.unitId ? `货币组件ID: ${mapping.unitId}` : '未设置货币组件' }} - 
                      {{ mapping.awardsKey || '未设置奖励Key' }}
                    </span>
                    <el-button 
                      size="mini" 
                      type="danger" 
                      icon="el-icon-delete" 
                      @click.stop="removeScratchCardMapping(index)"
                      style="margin-left: auto;">
                      删除
                    </el-button>
                  </div>
                </template>
                
                <el-form :inline="false" label-width="120px" class="collapse-content">
                  <el-row :gutter="16">
                    <el-col :span="12">
                      <el-form-item label="货币组件ID">
                        <el-input-number 
                          v-model="mapping.unitId" 
                          :min="0" 
                          placeholder="货币组件ID（可选）"
                          style="width: 100%">
                        </el-input-number>
                      </el-form-item>
                    </el-col>
                    <el-col :span="12">
                      <el-form-item label="奖励Key">
                        <reward-key-selector 
                          v-model="mapping.awardsKey" 
                          placeholder="请选择奖励Key（可选）">
                        </reward-key-selector>
                      </el-form-item>
                    </el-col>
                  </el-row>
                </el-form>
              </el-collapse-item>
            </el-collapse>
            
            <!-- 空状态 -->
            <el-empty v-if="formData.scratchCardUnit.mappingList.length === 0" description="暂无奖励映射" style="padding: 20px;">
              <el-button type="primary" @click="addScratchCardMapping">添加第一个映射</el-button>
            </el-empty>
          </div>

          <div style="font-size: 12px; color: #999; margin-top: 8px;">
            <p>• 奖池组件ID：刮刮乐使用的奖池组件</p>
            <p>• 其他数字奖池组件ID：其他数字奖池组件ID</p>
            <p>• 随机数范围：其他数字随机数的最小值和最大值</p>
            <p>• 奖励映射列表：配置奖励映射关系，每个映射可包含货币组件ID和奖励Key</p>
          </div>
        </el-card>
      </el-form>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" @click="handleSave">保存</el-button>
    </div>
    
    <!-- 导入组件数据对话框 -->
    <el-dialog title="导入组件数据" :visible.sync="importDialog.show" width="600px" append-to-body>
      <div class="import-dialog-content">
        <p style="margin-bottom: 10px; color: #666;">请粘贴复制的组件数据：</p>
        <el-input
          type="textarea"
          :rows="12"
          placeholder="请粘贴JSON格式的组件数据..."
          v-model="importDialog.dataText"
          style="margin-bottom: 15px;">
        </el-input>
        <div style="font-size: 12px; color: #999;">
          <p>提示：</p>
          <p>1. 请确保粘贴的是完整的JSON格式组件数据</p>
          <p>2. 导入后将自动填充表单，您可以修改相关信息后保存</p>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="importDialog.show = false">取消</el-button>
        <el-button type="primary" @click="importComponentData">确认导入</el-button>
      </div>
    </el-dialog>
    

  </el-dialog>
</template>

<script>
import RewardKeySelector from './RewardKeySelector.vue'
import CurrencyUnitSelector from './CurrencyUnitSelector.vue'
import UploadSingleImg from '@/components/UploadSingleImg/UploadSingleImg'
import { getChannelList } from '@/api/baseRequest'
import { 
  UNIT_TYPES, 
  CURRENCY_TYPES, 
  getUnitTypeName, 
  getCurrencyTypeName,
  getTaskTypeName,
  getTaskModeName,
  getTaskDurationName,
  getRankTypeName,
  getRankDurationName,
  getDefaultUnitConfigs,
  getUnitConfigKey
} from '@/utils/unitTypeConstants'

export default {
  name: 'UnitEditor',
  components: {
    RewardKeySelector,
    CurrencyUnitSelector,
    UploadSingleImg
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    unitData: {
      type: Object,
      default: () => ({})
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    allUnits: {
      type: Array,
      default: () => []
    },
    activityStartTime: {
      type: String,
      default: ''
    },
    activityEndTime: {
      type: String,
      default: ''
    },
    activityZoneOffset: {
      type: String,
      default: '+05:30'
    }
  },
  data() {
    return {
      dialogVisible: false,
      checkParamsSetStr: '',
      rankCheckParamsStr: '',
      // 组件类型常量
      UNIT_TYPES,
      CURRENCY_TYPES,
      // 榜单时间配置相关
      useDefaultRankTime: true,
      rankStartTime: null,
      rankEndTime: null,
      originalUnitData: null, // 保存原始数据的深拷贝
      // 导入对话框
      importDialog: {
        show: false,
        dataText: ''
      },

      // 折叠状态控制
      activeTaskConfigs: [],
      taskConfigsCollapsed: false,
      activeRankRewards: [],
      rankRewardsCollapsed: false,
      activeShopItems: [],
      shopItemsCollapsed: false,
      // 步频组件折叠状态
      activeSonPools: [],
      sonPoolsCollapsed: false,
      activeStepConfigs: [],
      stepConfigsCollapsed: false,
      activeStepRewards: [],
      stepRewardsCollapsed: false,
      // 新增：排行组件新字段折叠状态
      activeRankLabels: [],
      rankLabelsCollapsed: false,
      activeRankPoolConfigs: [],
      rankPoolConfigsCollapsed: false,
      // 刮刮乐组件折叠状态
      activeScratchCardMappings: [],
      scratchCardMappingsCollapsed: false,
      // 任务常量列表
      taskConstants: {
        taskTypes: [],
        taskModes: [],
        taskDurations: []
      },
      // 任务周期常量（用于商店和货币合成组件）
      taskDurationConstants: [],
      // 队伍周期常量列表
      teamDurationConstants: [],
      // 榜单类型常量
      rankTypeConstants: [],
      // 榜单周期类型常量
      rankCycleTypeConstants: [],
      // 渠道列表
      channelList: [],
      formData: {
        unitId: 1,
        unitType: 1,
        unitName: '',
        unitAka: '',
        unitDesc: '',
        weight: 1,
        currencyUnit: {
          icon: '',
          type: 9999,
          showRate: 1,
          enableAutoDraw: false,
          awardPoolId: 0,
          enableGive: false,
          giveConfig: {
            onceMax: 1
          }
        },
        awardPoolUnit: {
          costUnitIds: [],
          timesRate: 1,
          awardsKey: '',
          enablePayPool: false,
          minPayUsd: 0
        },
        taskUnit: {
          type: null,
          mode: null,
          duration: null,
          checkParamsSet: [],
          autoGiveAward: true,
          teamTask: false,
          teamUnitId: 0,
          config: {
            checkParams: 0,
            showRate: 1,
            checkText: '',
            limit: -1,
            awardsKey: '',
            autoSendNotice: false,
            notice: ''
          },
          configs: []
        },
        rankUnit: {
          rankType: 0,
          startTime: 0,
          endTime: 0,
          showTop: 10,
          awardTop: 10,
          scoreRate: 1,
          scoreIcon: '',
          checkParamsSet: [],
          autoGiveAward: false,
          autoSendGlobalNotice: false,
          rewardConfigs: [],
          noticeSupportChannel: [],
          enableRankLabel: false,
          rankLabelConfigs: [],
          rankDuration: 0,
          enablePool: false,
          teamRank: false,
          teamUnitId: 0,
          rankPoolInfo: {
            poolRate: 0,
            basePoolAmount: '',
            maxPoolAmount: '',
            showRate: 0,
            rankPoolConfigs: []
          },
          enableSonRank: false,
          sonRankConfig: {
            showTop: 1,
            awardTop: 1,
            diffSex: false
          }
        },
        teamUnit: {
          maxMember: 0,
          duration: 0,
          memberNotice: '',
          captainNotice: ''
        },
        stepPoolUnit: {
          maxSteps: 0,
          costUnitIds: [],
          refreshCost: 0,
          sonPools: [],
          stepConfigs: [],
          stepRewards: []
        },
        shopUnit: {
          costUnitIds: [],
          duration: 0,
          items: []
        },
        currencyMergeUnit: {
          costCurrencyUnitIds: [],
          duration: 0,
          limit: -1,
          awardsKey: ''
        },
        shareUnit: {
          costUnitIds: [],
          needCost: 0,
          shareAwardsKey: '',
          shareToAwardsKey: '',
          shareToNotice: ''
        },
        scratchCardUnit: {
          poolUnitId: 0,
          mappingList: [],
          lowPoolUnitId: 0,
          randomMin: 0,
          randomMax: 0
        }
      },
      rules: {
        unitId: [
          { required: true, message: '请输入组件ID', trigger: 'blur' },
          { validator: this.validateUnitId, trigger: 'blur' }
        ],
        unitType: [
          { required: true, message: '请选择组件类型', trigger: 'change' }
        ],
        unitName: [
          { required: true, message: '请输入组件名称', trigger: 'blur' }
        ],
        unitAka: [
          { required: true, message: '请输入组件别名', trigger: 'blur' }
        ]
      }
    }
  },
  watch: {
    value(val) {
      this.dialogVisible = val
      if (val) {
        if (this.isEdit && this.unitData) {
          // 保存原始数据的深拷贝
          this.originalUnitData = JSON.parse(JSON.stringify(this.unitData))
          this.initFormData()
        } else {
          this.resetFormData()
        }
      }
    },
    dialogVisible(val) {
      this.$emit('input', val)
    },
    // 监听校验参数字符串变化，同步到数组
    checkParamsSetStr(val) {
      if (this.formData.taskUnit) {
        if (val && val.trim()) {
          this.formData.taskUnit.checkParamsSet = this.parseIntArray(val)
        } else {
          this.formData.taskUnit.checkParamsSet = []
        }
      }
    },
    // 监听排行校验参数字符串变化，同步到数组
    rankCheckParamsStr(val) {
      if (this.formData.rankUnit) {
        if (val && val.trim()) {
          this.formData.rankUnit.checkParamsSet = this.parseStringArray(val)
        } else {
          this.formData.rankUnit.checkParamsSet = []
        }
      }
    },
    // 监听货币组件字段变化，确保响应性
    'formData.currencyUnit.enableGive': {
      handler(newVal) {
        console.log('enableGive changed:', newVal)
        // 强制更新视图
        this.$forceUpdate()
      },
      deep: true
    },
    'formData.currencyUnit.enableAutoDraw': {
      handler(newVal) {
        console.log('enableAutoDraw changed:', newVal)
        // 强制更新视图
        this.$forceUpdate()
      },
      deep: true
    }
  },
  methods: {
    // 验证组件ID唯一性
    validateUnitId(rule, value, callback) {
      // 检查值是否为有效数字
      if (!value || value <= 0) {
        callback(new Error('请输入有效的组件ID'));
        return;
      }

      // 如果是编辑模式，且组件ID与原始数据相同，则不验证唯一性
      if (this.isEdit && this.originalUnitData && this.originalUnitData.unitId === value) {
        callback();
        return;
      }

      // 检查组件ID是否已存在
      const isDuplicate = this.allUnits.some(unit => unit.unitId === value);
      if (isDuplicate) {
        callback(new Error('组件ID已存在，请重新选择'));
      } else {
        callback();
      }
    },
    
    // 验证单次赠送最大数量
    validateOnceMax() {
      // 只有在开启货币赠送功能时才验证
      if (!this.formData.currencyUnit || !this.formData.currencyUnit.enableGive) {
        return true;
      }
      
      const onceMax = this.formData.currencyUnit.giveConfig?.onceMax;
      
      // 检查值是否存在且大于0
      if (!onceMax || onceMax <= 0) {
        this.$message.error('单次赠送最大数量必须大于0');
        return false;
      }
      
      return true;
    },
    handleTypeChange() {
      if (this.formData.unitType === 8) {
        // 确保队伍组件的duration有正确的默认值
        if (this.formData.teamUnit.duration === null || this.formData.teamUnit.duration === undefined) {
          this.$set(this.formData.teamUnit, 'duration', 0)
        }
      }
      // 根据组件类型初始化对应的配置
      this.initUnitConfig()
    },
    // 显示导入对话框
    showImportDialog() {
      this.importDialog.show = true
      this.importDialog.dataText = ''
    },
    // 导入组件数据
    importComponentData() {
      try {
        if (!this.importDialog.dataText.trim()) {
          this.$message.error('请输入组件数据')
          return
        }
        
        // 解析JSON数据
        const componentData = JSON.parse(this.importDialog.dataText.trim())
        
        // 验证数据结构
        if (!componentData || typeof componentData !== 'object') {
          this.$message.error('数据格式不正确')
          return
        }
        
        // 确保有组件类型
        if (!componentData.unitType) {
          this.$message.error('缺少组件类型信息')
          return
        }
        
        // 先对导入的数据进行预处理，确保关键字段存在
        this.preprocessImportedData(componentData)
        
        // 合并数据到表单
        this.formData = { ...this.formData, ...componentData }
        
        // 根据组件类型初始化配置，确保数据结构完整
        this.initUnitConfig()
        
        // 重新初始化字符串字段（奖池组件costUnitId已改为直接管理数组）
        if (this.formData.taskUnit && this.formData.taskUnit.checkParamsSet && this.formData.taskUnit.checkParamsSet.length > 0) {
          this.checkParamsSetStr = this.formData.taskUnit.checkParamsSet.join(',')
        } else if (this.formData.taskUnit) {
          this.checkParamsSetStr = ''
        }
        
        if (this.formData.rankUnit && this.formData.rankUnit.checkParamsSet && this.formData.rankUnit.checkParamsSet.length > 0) {
          this.rankCheckParamsStr = this.formData.rankUnit.checkParamsSet.join(',')
        } else if (this.formData.rankUnit) {
          this.rankCheckParamsStr = ''
        }
        
        // 处理榜单时间配置
        if (this.formData.rankUnit) {
          if (this.formData.rankUnit.startTime && this.formData.rankUnit.startTime > 0) {
            this.useDefaultRankTime = false
            this.rankStartTime = this.formData.rankUnit.startTime * 1000 // 转换为毫秒
          }
          if (this.formData.rankUnit.endTime && this.formData.rankUnit.endTime > 0) {
            this.rankEndTime = this.formData.rankUnit.endTime * 1000 // 转换为毫秒
          }
        }
        
        // 关闭对话框
        this.importDialog.show = false
        this.importDialog.dataText = ''
        
        this.$message.success('组件数据导入成功')
        
      } catch (error) {
        console.error('导入组件数据错误:', error)
        this.$message.error('数据格式不正确，请检查JSON格式是否有效')
      }
    },
    // 预处理导入的数据，确保关键字段存在
    preprocessImportedData(componentData) {
      // 确保任务组件的mode字段存在
      if (componentData.taskUnit && componentData.taskUnit.mode === undefined) {
        componentData.taskUnit.mode = 0
      }
      
      // 确保任务组件的config对象存在
      if (componentData.taskUnit && !componentData.taskUnit.config) {
        componentData.taskUnit.config = {
          checkParams: 0,
          showRate: 1,
          checkText: '',
          limit: -1,
          awardsKey: '',
          autoSendNotice: false,
          notice: ''
        }
      }
      
      // 确保任务组件的configs数组存在
      if (componentData.taskUnit && !componentData.taskUnit.configs) {
        componentData.taskUnit.configs = []
      }
      
      // 确保排行组件的各种数组存在
      if (componentData.rankUnit) {
        if (!componentData.rankUnit.checkParamsSet) {
          componentData.rankUnit.checkParamsSet = []
        }
        if (!componentData.rankUnit.rewardConfigs) {
          componentData.rankUnit.rewardConfigs = []
        }
        // 确保每个奖励配置都有 sonRankAwardsKey 字段
        if (componentData.rankUnit.rewardConfigs && componentData.rankUnit.rewardConfigs.length > 0) {
          componentData.rankUnit.rewardConfigs.forEach(config => {
            if (config.sonRankAwardsKey === undefined) {
              config.sonRankAwardsKey = ''
            }
          })
        }
        if (!componentData.rankUnit.noticeSupportChannel) {
          componentData.rankUnit.noticeSupportChannel = []
        }
        // 新增：确保排行组件新字段存在
        if (componentData.rankUnit.enableRankLabel === undefined) {
          componentData.rankUnit.enableRankLabel = false
        }
        if (!componentData.rankUnit.rankLabelConfigs) {
          componentData.rankUnit.rankLabelConfigs = []
        }
        if (componentData.rankUnit.rankDuration === undefined) {
          componentData.rankUnit.rankDuration = 0
        }
        if (componentData.rankUnit.enablePool === undefined) {
          componentData.rankUnit.enablePool = false
        }
        if (!componentData.rankUnit.rankPoolInfo) {
          componentData.rankUnit.rankPoolInfo = {
            poolRate: 0,
            basePoolAmount: '',
            maxPoolAmount: '',
            showRate: 0,
            rankPoolConfigs: []
          }
        }
        if (!componentData.rankUnit.rankPoolInfo.rankPoolConfigs) {
          componentData.rankUnit.rankPoolInfo.rankPoolConfigs = []
        }
        // 新增：确保子榜单字段存在
        if (componentData.rankUnit.enableSonRank === undefined) {
          componentData.rankUnit.enableSonRank = false
        }
        if (!componentData.rankUnit.sonRankConfig) {
          componentData.rankUnit.sonRankConfig = {
            showTop: 1,
            awardTop: 1,
            diffSex: false
          }
        }
      }
      
      // 确保步频组件的各种数组存在
      if (componentData.stepPoolUnit) {
        if (!componentData.stepPoolUnit.costUnitIds) {
          componentData.stepPoolUnit.costUnitIds = []
        }
        if (!componentData.stepPoolUnit.sonPools) {
          componentData.stepPoolUnit.sonPools = []
        }
        if (!componentData.stepPoolUnit.stepConfigs) {
          componentData.stepPoolUnit.stepConfigs = []
        }
        if (!componentData.stepPoolUnit.stepRewards) {
          componentData.stepPoolUnit.stepRewards = []
        }
      }
      
      // 确保商店组件的各种数组存在
      if (componentData.shopUnit) {
        if (!componentData.shopUnit.costUnitIds) {
          componentData.shopUnit.costUnitIds = []
        }
        if (componentData.shopUnit.duration === undefined) {
          componentData.shopUnit.duration = 0
        }
        if (!componentData.shopUnit.items) {
          componentData.shopUnit.items = []
        }
        if (componentData.shopUnit.items) {
          componentData.shopUnit.items.forEach(item => {
            if (item.globalLimit === undefined) {
              item.globalLimit = -1
            }
          })
        }
      }
      
      // 确保货币组件的字段存在
      if (componentData.currencyUnit) {
        if (componentData.currencyUnit.enableAutoDraw === undefined) {
          componentData.currencyUnit.enableAutoDraw = false
        }
        if (componentData.currencyUnit.awardPoolId === undefined) {
          componentData.currencyUnit.awardPoolId = 0
        }
        if (componentData.currencyUnit.enableGive === undefined) {
          componentData.currencyUnit.enableGive = false
        }
        if (!componentData.currencyUnit.giveConfig) {
          componentData.currencyUnit.giveConfig = {
            onceMax: 1
          }
        }
      }
      
      // 确保奖池组件的数组存在，兼容旧字段名
      if (componentData.awardPoolUnit) {
        // 如果存在旧字段costUnitId，转换为新字段costUnitIds
        if (componentData.awardPoolUnit.costUnitId && !componentData.awardPoolUnit.costUnitIds) {
          componentData.awardPoolUnit.costUnitIds = componentData.awardPoolUnit.costUnitId
          delete componentData.awardPoolUnit.costUnitId
        }
        // 确保costUnitIds存在
        if (!componentData.awardPoolUnit.costUnitIds) {
          componentData.awardPoolUnit.costUnitIds = []
        }
        // 新增：确保三方充值分奖池字段存在
        if (componentData.awardPoolUnit.enablePayPool === undefined) {
          componentData.awardPoolUnit.enablePayPool = false
        }
        if (componentData.awardPoolUnit.minPayUsd === undefined) {
          componentData.awardPoolUnit.minPayUsd = 0
        }
      }
      
      // 确保货币合成组件的字段存在
      if (componentData.currencyMergeUnit) {
        if (componentData.currencyMergeUnit.duration === undefined) {
          componentData.currencyMergeUnit.duration = 0
        }
        if (componentData.currencyMergeUnit.limit === undefined) {
          componentData.currencyMergeUnit.limit = -1
        }
      }
      
      // 新增：确保分享奖励组件的字段存在
      if (componentData.shareUnit) {
        if (!componentData.shareUnit.costUnitIds) {
          componentData.shareUnit.costUnitIds = []
        }
        if (componentData.shareUnit.needCost === undefined) {
          componentData.shareUnit.needCost = 0
        }
        if (!componentData.shareUnit.shareAwardsKey) {
          componentData.shareUnit.shareAwardsKey = ''
        }
        if (!componentData.shareUnit.shareToAwardsKey) {
          componentData.shareUnit.shareToAwardsKey = ''
        }
        if (!componentData.shareUnit.shareToNotice) {
          componentData.shareUnit.shareToNotice = ''
        }
      }
      
      // 新增：确保刮刮乐组件的字段存在
      if (componentData.scratchCardUnit) {
        if (componentData.scratchCardUnit.poolUnitId === undefined) {
          componentData.scratchCardUnit.poolUnitId = 0
        }
        if (!Array.isArray(componentData.scratchCardUnit.mappingList)) {
          componentData.scratchCardUnit.mappingList = []
        }
        // 确保mappingList中的每个对象都有必要的字段
        if (componentData.scratchCardUnit.mappingList) {
          componentData.scratchCardUnit.mappingList.forEach(mapping => {
            if (mapping.unitId === undefined) {
              mapping.unitId = null
            }
            if (!mapping.awardsKey) {
              mapping.awardsKey = ''
            }
          })
        }
        if (componentData.scratchCardUnit.lowPoolUnitId === undefined) {
          componentData.scratchCardUnit.lowPoolUnitId = 0
        }
        if (componentData.scratchCardUnit.randomMin === undefined) {
          componentData.scratchCardUnit.randomMin = 0
        }
        if (componentData.scratchCardUnit.randomMax === undefined) {
          componentData.scratchCardUnit.randomMax = 0
        }
      }
    },
    // === 数据初始化优化 ===
    initUnitConfig() {
      const defaultConfigs = this.getDefaultUnitConfigs()
      const type = this.formData.unitType
      const currentConfigKey = this.getUnitConfigKey(type)
      
      // 只为当前组件类型补充默认配置，保留已有数据
      Object.keys(defaultConfigs).forEach(key => {
        if (key === currentConfigKey) {
          // 对于当前组件类型，合并默认配置（保留已有数据）
          if (!this.formData[key]) {
            this.$set(this.formData, key, { ...defaultConfigs[key] })
          } else {
            // 逐个字段合并，保证响应性
            Object.keys(defaultConfigs[key]).forEach(field => {
              if (this.formData[key][field] === undefined || this.formData[key][field] === null) {
                this.$set(this.formData[key], field, defaultConfigs[key][field])
              }
            })
          }
        } else {
          // 对于其他组件类型，如果不存在则初始化为默认值
          if (!this.formData[key]) {
            this.$set(this.formData, key, { ...defaultConfigs[key] })
          }
        }
      })
      
      // 特殊处理：确保队伍组件的duration不为null
      if (this.formData.teamUnit && (this.formData.teamUnit.duration === null || this.formData.teamUnit.duration === undefined)) {
        this.$set(this.formData.teamUnit, 'duration', 0)
      }
    },
    
    // 获取组件类型对应的配置键名
    getUnitConfigKey(unitType) {
      return getUnitConfigKey(unitType)
    },
    
    // 获取默认组件配置
    getDefaultUnitConfigs() {
      return getDefaultUnitConfigs()
    },
    addShopItem() {
      const newIndex = this.formData.shopUnit.items.length
      this.formData.shopUnit.items.push({
        awardsKey: '',
        showPrice: '',
        price: 0,
        weight: 100,
        globalLimit: -1
      })
      
      // 自动展开新添加的项目
      this.$nextTick(() => {
        if (!this.activeShopItems.includes(`shop-${newIndex}`)) {
          this.activeShopItems.push(`shop-${newIndex}`)
        }
      })
    },
    removeShopItem(index) {
      this.formData.shopUnit.items.splice(index, 1)
      
      // 更新折叠状态
      this.activeShopItems = this.activeShopItems.filter(name => name !== `shop-${index}`)
      // 重新索引后面的项目
      this.activeShopItems = this.activeShopItems.map(name => {
        const match = name.match(/shop-(\d+)/)
        if (match) {
          const itemIndex = parseInt(match[1])
          return itemIndex > index ? `shop-${itemIndex - 1}` : name
        }
        return name
      })
    },
    // 任务组件多节点配置方法
    addTaskConfig() {
      const newIndex = this.formData.taskUnit.configs.length
      this.formData.taskUnit.configs.push({
        checkParams: 0,
        showRate: 1,
        checkText: '',
        limit: -1,
        awardsKey: '',
        autoSendNotice: false,
        notice: ''
      })
      
      // 自动展开新添加的项目
      this.$nextTick(() => {
        if (!this.activeTaskConfigs.includes(`task-${newIndex}`)) {
          this.activeTaskConfigs.push(`task-${newIndex}`)
        }
      })
    },
    removeTaskConfig(index) {
      this.formData.taskUnit.configs.splice(index, 1)
      
      // 更新折叠状态
      this.activeTaskConfigs = this.activeTaskConfigs.filter(name => name !== `task-${index}`)
      // 重新索引后面的项目
      this.activeTaskConfigs = this.activeTaskConfigs.map(name => {
        const match = name.match(/task-(\d+)/)
        if (match) {
          const itemIndex = parseInt(match[1])
          return itemIndex > index ? `task-${itemIndex - 1}` : name
        }
        return name
      })
    },
    // 排行组件奖励配置方法
    addRankReward() {
      const newIndex = this.formData.rankUnit.rewardConfigs.length
      this.formData.rankUnit.rewardConfigs.push({
        minTop: 1,
        awardsKey: '',
        sonRankAwardsKey: ''
      })
      
      // 自动展开新添加的项目
      this.$nextTick(() => {
        if (!this.activeRankRewards.includes(`rank-${newIndex}`)) {
          this.activeRankRewards.push(`rank-${newIndex}`)
        }
      })
    },
    removeRankReward(index) {
      this.formData.rankUnit.rewardConfigs.splice(index, 1)
      
      // 更新折叠状态
      this.activeRankRewards = this.activeRankRewards.filter(name => name !== `rank-${index}`)
      // 重新索引后面的项目
      this.activeRankRewards = this.activeRankRewards.map(name => {
        const match = name.match(/rank-(\d+)/)
        if (match) {
          const itemIndex = parseInt(match[1])
          return itemIndex > index ? `rank-${itemIndex - 1}` : name
        }
        return name
      })
    },
    // 折叠控制方法
    toggleTaskConfigsCollapse() {
      this.taskConfigsCollapsed = !this.taskConfigsCollapsed
      if (this.taskConfigsCollapsed) {
        this.activeTaskConfigs = []
      } else {
        this.activeTaskConfigs = this.formData.taskUnit.configs.map((_, index) => `task-${index}`)
      }
    },
    toggleRankRewardsCollapse() {
      this.rankRewardsCollapsed = !this.rankRewardsCollapsed
      if (this.rankRewardsCollapsed) {
        this.activeRankRewards = []
      } else {
        this.activeRankRewards = this.formData.rankUnit.rewardConfigs.map((_, index) => `rank-${index}`)
      }
    },
    toggleShopItemsCollapse() {
      this.shopItemsCollapsed = !this.shopItemsCollapsed
      if (this.shopItemsCollapsed) {
        this.activeShopItems = []
      } else {
        this.activeShopItems = this.formData.shopUnit.items.map((_, index) => `shop-${index}`)
      }
    },
    // 刮刮乐组件映射列表方法
    addScratchCardMapping() {
      const newIndex = this.formData.scratchCardUnit.mappingList.length
      this.formData.scratchCardUnit.mappingList.push({
        unitId: null,
        awardsKey: ''
      })
      
      // 自动展开新添加的项目
      this.$nextTick(() => {
        if (!this.activeScratchCardMappings.includes(`mapping-${newIndex}`)) {
          this.activeScratchCardMappings.push(`mapping-${newIndex}`)
        }
      })
    },
    removeScratchCardMapping(index) {
      this.formData.scratchCardUnit.mappingList.splice(index, 1)
      
      // 更新折叠状态
      this.activeScratchCardMappings = this.activeScratchCardMappings.filter(name => name !== `mapping-${index}`)
      // 重新索引后面的项目
      this.activeScratchCardMappings = this.activeScratchCardMappings.map(name => {
        const match = name.match(/mapping-(\d+)/)
        if (match) {
          const itemIndex = parseInt(match[1])
          return itemIndex > index ? `mapping-${itemIndex - 1}` : name
        }
        return name
      })
    },
    toggleScratchCardMappingsCollapse() {
      this.scratchCardMappingsCollapsed = !this.scratchCardMappingsCollapsed
      if (this.scratchCardMappingsCollapsed) {
        this.activeScratchCardMappings = []
      } else {
        this.activeScratchCardMappings = this.formData.scratchCardUnit.mappingList.map((_, index) => `mapping-${index}`)
      }
    },
    initFormData() {
      // 使用深拷贝避免引用问题
      this.formData = JSON.parse(JSON.stringify(this.unitData))
      
      // 确保所有组件类型的对象都存在，避免模板中访问时出现 null 错误
      if (!this.formData.currencyUnit) {
        this.$set(this.formData, 'currencyUnit', {
          icon: '',
          type: 9999,
          showRate: 1,
          enableAutoDraw: false,
          awardPoolId: 0,
          enableGive: false,
          giveConfig: {
            onceMax: 1
          }
        })
      }
      
      // 确保新增字段存在，使用$set确保响应性
      if (this.formData.currencyUnit) {
        if (this.formData.currencyUnit.enableAutoDraw === undefined) {
          this.$set(this.formData.currencyUnit, 'enableAutoDraw', false)
        }
        if (this.formData.currencyUnit.awardPoolId === undefined) {
          this.$set(this.formData.currencyUnit, 'awardPoolId', 0)
        }
        if (this.formData.currencyUnit.enableGive === undefined) {
          this.$set(this.formData.currencyUnit, 'enableGive', false)
        }
        if (!this.formData.currencyUnit.giveConfig) {
          this.$set(this.formData.currencyUnit, 'giveConfig', {
            onceMax: 1
          })
        }
      }
      
      if (!this.formData.awardPoolUnit) {
        this.$set(this.formData, 'awardPoolUnit', {
          costUnitIds: [],
          timesRate: 1,
          awardsKey: ''
        })
      } else {
        // 兼容处理：如果存在旧字段costUnitId，转换为新字段costUnitIds
        if (this.formData.awardPoolUnit.costUnitId && !this.formData.awardPoolUnit.costUnitIds) {
          this.$set(this.formData.awardPoolUnit, 'costUnitIds', this.formData.awardPoolUnit.costUnitId)
          delete this.formData.awardPoolUnit.costUnitId
        }
        // 确保costUnitIds存在
        if (!this.formData.awardPoolUnit.costUnitIds) {
          this.$set(this.formData.awardPoolUnit, 'costUnitIds', [])
        }
        // 新增：确保三方充值分奖池字段存在
        if (this.formData.awardPoolUnit.enablePayPool === undefined) {
          this.$set(this.formData.awardPoolUnit, 'enablePayPool', false)
        }
        if (this.formData.awardPoolUnit.minPayUsd === undefined) {
          this.$set(this.formData.awardPoolUnit, 'minPayUsd', 0)
        }
      }
      
      if (!this.formData.taskUnit) {
        this.formData.taskUnit = {
          type: 0,
          mode: 0,
          duration: null,
          checkParamsSet: [],
          autoGiveAward: true,
          teamTask: false,
          teamUnitId: 0,
          config: {
            checkParams: 0,
            showRate: 1,
            checkText: '',
            limit: -1,
            awardsKey: '',
            autoSendNotice: false,
            notice: ''
          },
          configs: []
        }
      }
      
      if (!this.formData.rankUnit) {
        this.formData.rankUnit = {
          rankType: 0,
          startTime: 0,
          endTime: 0,
          showTop: 10,
          awardTop: 10,
          scoreRate: 1,
          scoreIcon: '',
          checkParamsSet: [],
          autoGiveAward: false,
          autoSendGlobalNotice: false,
          rewardConfigs: [],
          noticeSupportChannel: []
        }
      }
      
      // 新增：确保 rankUnit 的新字段存在
      if (this.formData.rankUnit) {
        if (this.formData.rankUnit.enableSonRank === undefined) {
          this.$set(this.formData.rankUnit, 'enableSonRank', false)
        }
        if (!this.formData.rankUnit.sonRankConfig) {
          this.$set(this.formData.rankUnit, 'sonRankConfig', {
            showTop: 1,
            awardTop: 1,
            diffSex: false
          })
        }
        // 确保每个奖励配置都有 sonRankAwardsKey 字段
        if (this.formData.rankUnit.rewardConfigs && this.formData.rankUnit.rewardConfigs.length > 0) {
          this.formData.rankUnit.rewardConfigs.forEach(config => {
            if (config.sonRankAwardsKey === undefined) {
              this.$set(config, 'sonRankAwardsKey', '')
            }
          })
        }
      }
      
      if (!this.formData.stepPoolUnit) {
        this.$set(this.formData, 'stepPoolUnit', {
          maxSteps: 0,
          costUnitIds: [],
          refreshCost: 0,
          sonPools: [],
          stepConfigs: [],
          stepRewards: []
        })
      } else {
        // 确保costUnitIds存在
        if (!this.formData.stepPoolUnit.costUnitIds) {
          this.$set(this.formData.stepPoolUnit, 'costUnitIds', [])
        }
      }
      
      if (!this.formData.shopUnit) {
        this.$set(this.formData, 'shopUnit', {
          costUnitIds: [],
          duration: 0,
          items: []
        })
      } else {
        // 确保costUnitIds存在
        if (!this.formData.shopUnit.costUnitIds) {
          this.$set(this.formData.shopUnit, 'costUnitIds', [])
        }
        // 确保duration存在
        if (this.formData.shopUnit.duration === undefined) {
          this.$set(this.formData.shopUnit, 'duration', 0)
        }
        // 确保items中的globalLimit存在
        if (this.formData.shopUnit.items) {
          this.formData.shopUnit.items.forEach(item => {
            if (item.globalLimit === undefined) {
              this.$set(item, 'globalLimit', -1)
            }
          })
        }
      }
      
      if (!this.formData.currencyMergeUnit) {
        this.$set(this.formData, 'currencyMergeUnit', {
          costCurrencyUnitIds: [],
          duration: 0,
          limit: -1,
          awardsKey: ''
        })
      } else {
        // 确保costCurrencyUnitIds存在
        if (!this.formData.currencyMergeUnit.costCurrencyUnitIds) {
          this.$set(this.formData.currencyMergeUnit, 'costCurrencyUnitIds', [])
        }
        // 确保duration存在
        if (this.formData.currencyMergeUnit.duration === undefined) {
          this.$set(this.formData.currencyMergeUnit, 'duration', 0)
        }
        // 确保limit存在
        if (this.formData.currencyMergeUnit.limit === undefined) {
          this.$set(this.formData.currencyMergeUnit, 'limit', -1)
        }
      }
      
      if (!this.formData.teamUnit) {
        this.$set(this.formData, 'teamUnit', {
          maxMember: 0,
          duration: 0,
          memberNotice: '',
          captainNotice: ''
        })
      } else {
        // 确保新字段存在
        if (this.formData.teamUnit.memberNotice === undefined) {
          this.$set(this.formData.teamUnit, 'memberNotice', '')
        }
        if (this.formData.teamUnit.captainNotice === undefined) {
          this.$set(this.formData.teamUnit, 'captainNotice', '')
        }
      }
      
      // 新增：确保分享奖励组件存在
      if (!this.formData.shareUnit) {
        this.$set(this.formData, 'shareUnit', {
          costUnitIds: [],
          needCost: 0,
          shareAwardsKey: '',
          shareToAwardsKey: '',
          shareToNotice: ''
        })
      } else {
        // 确保costUnitIds存在
        if (!this.formData.shareUnit.costUnitIds) {
          this.$set(this.formData.shareUnit, 'costUnitIds', [])
        }
      }
      
      // 新增：确保刮刮乐组件存在
      if (!this.formData.scratchCardUnit) {
        this.$set(this.formData, 'scratchCardUnit', {
          poolUnitId: 0,
          mappingList: [],
          lowPoolUnitId: 0,
          randomMin: 0,
          randomMax: 0
        })
      } else {
        // 确保所有字段存在
        if (this.formData.scratchCardUnit.poolUnitId === undefined) {
          this.$set(this.formData.scratchCardUnit, 'poolUnitId', 0)
        }
        if (!Array.isArray(this.formData.scratchCardUnit.mappingList)) {
          this.$set(this.formData.scratchCardUnit, 'mappingList', [])
        }
        // 确保mappingList中的每个对象都有必要的字段
        if (this.formData.scratchCardUnit.mappingList) {
          this.formData.scratchCardUnit.mappingList.forEach((mapping, index) => {
            if (mapping.unitId === undefined) {
              this.$set(mapping, 'unitId', null)
            }
            if (!mapping.awardsKey) {
              this.$set(mapping, 'awardsKey', '')
            }
          })
        }
        if (this.formData.scratchCardUnit.lowPoolUnitId === undefined) {
          this.$set(this.formData.scratchCardUnit, 'lowPoolUnitId', 0)
        }
        if (this.formData.scratchCardUnit.randomMin === undefined) {
          this.$set(this.formData.scratchCardUnit, 'randomMin', 0)
        }
        if (this.formData.scratchCardUnit.randomMax === undefined) {
          this.$set(this.formData.scratchCardUnit, 'randomMax', 0)
        }
      }
      
      // 处理字符串格式的数组字段
      if (this.formData.taskUnit && this.formData.taskUnit.checkParamsSet && this.formData.taskUnit.checkParamsSet.length > 0) {
        this.checkParamsSetStr = this.formData.taskUnit.checkParamsSet.join(',')
      } else if (this.formData.taskUnit) {
        // 确保数组为空时，字符串也为空
        this.checkParamsSetStr = ''
      }
      
      if (this.formData.rankUnit && this.formData.rankUnit.checkParamsSet && this.formData.rankUnit.checkParamsSet.length > 0) {
        this.rankCheckParamsStr = this.formData.rankUnit.checkParamsSet.join(',')
      } else if (this.formData.rankUnit) {
        // 确保数组为空时，字符串也为空
        this.rankCheckParamsStr = ''
      }
      
      // 处理榜单时间配置
      if (this.formData.rankUnit) {
        if (this.formData.rankUnit.startTime && this.formData.rankUnit.startTime > 0) {
          this.useDefaultRankTime = false
          this.rankStartTime = this.formData.rankUnit.startTime * 1000 // 转换为毫秒
        } else {
          this.useDefaultRankTime = true
          this.rankStartTime = null
        }
        
        if (this.formData.rankUnit.endTime && this.formData.rankUnit.endTime > 0) {
          this.rankEndTime = this.formData.rankUnit.endTime * 1000 // 转换为毫秒
        } else {
          this.rankEndTime = null
        }
      }
      
      // 确保数组格式正确
      if (this.formData.awardPoolUnit && !Array.isArray(this.formData.awardPoolUnit.costUnitIds)) {
        this.$set(this.formData.awardPoolUnit, 'costUnitIds', [])
      }
      
      if (this.formData.stepPoolUnit && !Array.isArray(this.formData.stepPoolUnit.costUnitIds)) {
        this.$set(this.formData.stepPoolUnit, 'costUnitIds', [])
      }
      
      if (this.formData.shopUnit && !Array.isArray(this.formData.shopUnit.costUnitIds)) {
        this.$set(this.formData.shopUnit, 'costUnitIds', [])
      }
      
      if (this.formData.currencyMergeUnit && !Array.isArray(this.formData.currencyMergeUnit.costCurrencyUnitIds)) {
        this.$set(this.formData.currencyMergeUnit, 'costCurrencyUnitIds', [])
      }
    },
    resetFormData() {
      this.formData = {
        unitId: 1,
        unitType: 1,
        unitName: '',
        unitAka: '',
        unitDesc: '',
        weight: 1,
        currencyUnit: {
          icon: '',
          type: 9999,
          showRate: 1,
          enableAutoDraw: false,
          awardPoolId: 0,
          enableGive: false,
          giveConfig: {
            onceMax: 1
          }
        },
        awardPoolUnit: {
          costUnitIds: [],
          timesRate: 1,
          awardsKey: ''
        },
        taskUnit: {
          type: null,
          mode: null,
          duration: null,
          checkParamsSet: [],
          autoGiveAward: true,
          teamTask: false,
          teamUnitId: 0,
          config: {
            checkParams: 0,
            showRate: 1,
            checkText: '',
            limit: -1,
            awardsKey: '',
            autoSendNotice: false,
            notice: ''
          },
          configs: []
        },
        rankUnit: {
          rankType: 0,
          startTime: 0,
          endTime: 0,
          showTop: 10,
          awardTop: 10,
          scoreRate: 1,
          scoreIcon: '',
          checkParamsSet: [],
          autoGiveAward: false,
          autoSendGlobalNotice: false,
          rewardConfigs: [],
          noticeSupportChannel: [],
          enableRankLabel: false,
          rankLabelConfigs: [],
          rankDuration: 0,
          enablePool: false,
          teamRank: false,
          teamUnitId: 0,
          rankPoolInfo: {
            poolRate: 0,
            basePoolAmount: '',
            maxPoolAmount: '',
            showRate: 0,
            rankPoolConfigs: []
          },
          enableSonRank: false,
          sonRankConfig: {
            showTop: 1,
            awardTop: 1,
            diffSex: false
          }
        },
        teamUnit: {
          maxMember: 0,
          duration: 0,
          memberNotice: '',
          captainNotice: ''
        },
        stepPoolUnit: {
          maxSteps: 0,
          costUnitIds: [],
          refreshCost: 0,
          sonPools: [],
          stepConfigs: [],
          stepRewards: []
        },
        shopUnit: {
          costUnitIds: [],
          duration: 0,
          items: []
        },
        currencyMergeUnit: {
          costCurrencyUnitIds: [],
          duration: 0,
          limit: -1,
          awardsKey: ''
        },
        shareUnit: {
          costUnitIds: [],
          needCost: 0,
          shareAwardsKey: '',
          shareToAwardsKey: '',
          shareToNotice: ''
        }
      }
      
      this.checkParamsSetStr = ''
      this.rankCheckParamsStr = ''
      this.useDefaultRankTime = true
      this.rankStartTime = null
      this.rankEndTime = null
    },
    handleClose() {
      // 如果是编辑模式且有原始数据，恢复到原始状态
      if (this.isEdit && this.originalUnitData) {
        this.initFormData()
      }
      this.dialogVisible = false
    },
    // 获取任务常量数据
    async getTaskConstants() {
      try {
        const { getConstList } = await import('@/api/constRequest')
        
        const [taskTypeRes, taskModeRes, taskDurationRes] = await Promise.all([
          getConstList('taskType'),
          getConstList('taskMode'), 
          getConstList('taskDuration')
        ])
        
        if (taskTypeRes.code === 0) this.taskConstants.taskTypes = taskTypeRes.data
        if (taskModeRes.code === 0) this.taskConstants.taskModes = taskModeRes.data
        if (taskDurationRes.code === 0) {
          this.taskConstants.taskDurations = taskDurationRes.data
          this.taskDurationConstants = taskDurationRes.data // 用于商店和货币合成组件
        }
        
      } catch (error) {
        console.error('获取任务常量数据失败:', error)
      }
    },
    
    // 获取队伍周期常量
    async getTeamDurationConstants() {
      try {
        const { getConstList } = await import('@/api/constRequest')
        
        const teamDurationRes = await getConstList('taskDuration')
        console.log('队伍周期常量数据获取成功:', teamDurationRes)
        if (teamDurationRes.code === 0) {
          this.teamDurationConstants = teamDurationRes.data
          console.log('队伍周期常量数据获取成功:', this.teamDurationConstants)
        } else {
          console.error('获取队伍周期常量失败:', teamDurationRes)
        }
        
      } catch (error) {
        console.error('获取队伍周期常量数据失败:', error)
      }
    },
    
    // 获取榜单类型常量
    async getRankTypeConstants() {
      try {
        const { getConstList } = await import('@/api/constRequest')
        const res = await getConstList('activityRankType')
        if (res.code === 0) {
          this.rankTypeConstants = res.data
        }
      } catch (error) {
        console.error('获取榜单类型常量失败:', error)
        // 如果获取失败，使用默认数据
        this.rankTypeConstants = [
          { value: 1001, name: '送礼钻石榜' },
          { value: 1002, name: '收礼钻石榜' },
          { value: 1003, name: '财富榜' },
          { value: 1004, name: '魅力榜' },
          { value: 1010, name: '收礼价值榜' },
          { value: 1020, name: '获取活动货币榜' }
        ]
      }
    },
    
    // 获取榜单周期类型常量
    async getRankCycleTypeConstants() {
      try {
        const { getConstList } = await import('@/api/constRequest')
        const res = await getConstList('rankCycleType')
        if (res.code === 0) {
          this.rankCycleTypeConstants = res.data
        }
      } catch (error) {
        console.error('获取榜单周期类型常量失败:', error)
        // 如果获取失败，使用默认数据
        this.rankCycleTypeConstants = [
          { value: 0, name: '默认类型（指定时间）' },
          { value: 1, name: '每日榜单' },
          { value: 2, name: '每周榜单' },
          { value: 3, name: '每月榜单' }
        ]
      }
    },
    
    // 上传积分图标
    async uploadRankScoreIcon(file) {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('cover', 0)
      formData.append('dir', '/rank_score/unit/event/')
      const { data: res } = await this.$axios.post(
        this.$store.getters.path.hostmgr_upload +
        '?username=' +
        this.$store.getters.username +
        '&token=' +
        this.$store.getters.token,
        formData
      )
      
      if (res.code === 0) {
        this.formData.rankUnit.scoreIcon = res.data
        this.$message.success('积分图标上传成功')
      } else {
        this.$message.error('积分图标上传失败: ' + res.msg)
      }
      return { code: res.code, url: res.data }
    },
    
    // 上传货币图标
    async uploadCurrencyIcon(file) {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('cover', 0)
      formData.append('dir', 'currency/unit/event/')
      const { data: res } = await this.$axios.post(
        this.$store.getters.path.hostmgr_upload +
        '?username=' +
        this.$store.getters.username +
        '&token=' +
        this.$store.getters.token,
        formData
      )
      return { code: res.code, url: res.data }
    },
    

    
    // === 通用列表管理方法 ===
    // 通用添加项目方法
    addListItem(listPath, defaultItem, activeListName, prefix) {
      const list = this.getNestedProperty(this.formData, listPath)
      list.push(defaultItem)
      this.$nextTick(() => {
        this[activeListName].push(`${prefix}-${list.length - 1}`)
      })
    },
    
    // 通用删除项目方法
    removeListItem(listPath, index, activeListName) {
      const list = this.getNestedProperty(this.formData, listPath)
      list.splice(index, 1)
      this[activeListName] = []
    },
    
    // 通用折叠切换方法
    toggleListCollapse(collapsedFlag, activeListName, listPath, prefix) {
      this[collapsedFlag] = !this[collapsedFlag]
      if (this[collapsedFlag]) {
        this[activeListName] = []
      } else {
        const list = this.getNestedProperty(this.formData, listPath)
        this[activeListName] = list.map((_, index) => `${prefix}-${index}`)
      }
    },
    
    // 获取嵌套属性的辅助方法
    getNestedProperty(obj, path) {
      return path.split('.').reduce((current, key) => current && current[key], obj)
    },
    
    // === 显示优化方法 ===
    // 获取任务类型显示名称
    getTaskTypeName(value) {
      return getTaskTypeName(value, this.taskConstants.taskTypes)
    },
    
    // 获取任务方式显示名称
    getTaskModeName(value) {
      return getTaskModeName(value, this.taskConstants.taskModes)
    },
    
    // 获取任务周期显示名称
    getTaskDurationName(value) {
      return getTaskDurationName(value, this.taskConstants.taskDurations)
    },
    
    // 获取队伍周期显示名称
    getTeamDurationName(value) {
      return getTaskDurationName(value, this.teamDurationConstants)
    },
    
    // 获取榜单类型显示名称
    getRankTypeName(value) {
      return getRankTypeName(value, this.rankTypeConstants)
    },
    
    // 获取榜单周期类型显示名称
    getRankDurationName(value) {
      return getRankDurationName(value, this.rankCycleTypeConstants)
    },
    
    // === 子奖池管理方法 ===
    addSonPool() {
      this.addListItem(
        'stepPoolUnit.sonPools',
        { poolId: '', poolName: '', awardsKey: '' },
        'activeSonPools',
        'pool'
      )
    },
    
    removeSonPool(index) {
      this.removeListItem('stepPoolUnit.sonPools', index, 'activeSonPools')
    },
    
    toggleSonPoolsCollapse() {
      this.toggleListCollapse(
        'sonPoolsCollapsed',
        'activeSonPools', 
        'stepPoolUnit.sonPools',
        'pool'
      )
    },
    
    // === 步频配置管理方法 ===
    addStepConfig() {
      const stepConfigs = this.formData.stepPoolUnit.stepConfigs
      this.addListItem(
        'stepPoolUnit.stepConfigs',
        {
          step: stepConfigs.length + 1,
          poolId: '',
          cost: 0,
          remainingCost: 0
        },
        'activeStepConfigs',
        'stepconfig'
      )
    },
    
    removeStepConfig(index) {
      this.removeListItem('stepPoolUnit.stepConfigs', index, 'activeStepConfigs')
    },
    
    toggleStepConfigsCollapse() {
      this.toggleListCollapse(
        'stepConfigsCollapsed',
        'activeStepConfigs',
        'stepPoolUnit.stepConfigs', 
        'stepconfig'
      )
    },
    
    // === 步频奖励管理方法 ===
    addStepReward() {
      const stepRewards = this.formData.stepPoolUnit.stepRewards
      this.addListItem(
        'stepPoolUnit.stepRewards',
        {
          step: stepRewards.length + 1,
          awardsKey: '',
          notice: ''
        },
        'activeStepRewards',
        'stepreward'
      )
    },
    
    removeStepReward(index) {
      this.removeListItem('stepPoolUnit.stepRewards', index, 'activeStepRewards')
    },
    
    toggleStepRewardsCollapse() {
      this.toggleListCollapse(
        'stepRewardsCollapsed',
        'activeStepRewards',
        'stepPoolUnit.stepRewards',
        'stepreward'
      )
    },
    
    // === 数据验证方法 ===
    // 验证步频组件数据
    validateStepPoolData() {
      const stepPoolUnit = this.formData.stepPoolUnit
      if (!stepPoolUnit) return true
      
      // 验证最大步频数量
      if (stepPoolUnit.maxSteps <= 0) {
        this.$message.error('最大步频数量必须大于0')
        return false
      }
      
      if (stepPoolUnit.maxSteps > 20) {
        this.$message.error('最大步频数量不能超过20')
        return false
      }
      
      // 验证步频配置
      if (stepPoolUnit.stepConfigs) {
        for (let config of stepPoolUnit.stepConfigs) {
          if (!config.step || config.step <= 0) {
            this.$message.error('步频序号必须大于0')
            return false
          }
          if (config.cost < 0) {
            this.$message.error('步频消耗不能小于0')
            return false
          }
        }
      }
      
      return true
    },
    
    // 验证商店组件数据
    validateShopData() {
      const shopUnit = this.formData.shopUnit
      if (!shopUnit || !shopUnit.items) return true
      
      // 验证duration必填
      if (shopUnit.duration === undefined || shopUnit.duration === null) {
        this.$message.error('商店组件必须选择限制条件使用周期')
        return false
      }
      
      for (let item of shopUnit.items) {
        if (!item.awardsKey) {
          this.$message.error('商品必须设置奖励Key')
          return false
        }
        // if (!item.showPrice) {
        //   this.$message.error('商品必须设置显示价格')
        //   return false
        // }
        if (item.price <= 0) {
          this.$message.error('商品价格必须大于0')
          return false
        }
      }
      
      return true
    },
    
    validateAwardPoolData() {
      const awardPoolUnit = this.formData.awardPoolUnit
      if (!awardPoolUnit) return true
      
      // 验证消耗货币组件
      if (!awardPoolUnit.costUnitIds || awardPoolUnit.costUnitIds.length === 0) {
        this.$message.error('奖池组件必须选择至少一个消耗货币组件')
        return false
      }
      
      // 验证次数汇率
      if (!awardPoolUnit.timesRate || awardPoolUnit.timesRate <= 0) {
        this.$message.error('次数汇率必须大于0')
        return false
      }
      
      // 验证奖励Key
      if (!awardPoolUnit.awardsKey) {
        this.$message.error('奖池组件必须设置奖励Key')
        return false
      }
      
      return true
    },
    
    validateCurrencyMergeData() {
      const currencyMergeUnit = this.formData.currencyMergeUnit
      if (!currencyMergeUnit) return true
      
      // 验证消耗的货币组件
      if (!currencyMergeUnit.costCurrencyUnitIds || currencyMergeUnit.costCurrencyUnitIds.length === 0) {
        this.$message.error('货币合成组件必须选择至少一个消耗货币组件')
        return false
      }
      
      // 验证duration必填
      if (currencyMergeUnit.duration === undefined || currencyMergeUnit.duration === null) {
        this.$message.error('货币合成组件必须选择周期')
        return false
      }
      
      // 验证limit必填
      if (currencyMergeUnit.limit === undefined || currencyMergeUnit.limit === null) {
        this.$message.error('货币合成组件必须设置周期内最大合成次数')
        return false
      }
      
      // 验证奖励Key
      if (!currencyMergeUnit.awardsKey) {
        this.$message.error('货币合成组件必须设置奖励Key')
        return false
      }
      
      return true
    },
    
    // 验证任务组件数据
    validateTaskData() {
      const taskUnit = this.formData.taskUnit
      if (!taskUnit) return true
      
      // 检查多节点配置
      if (taskUnit.configs && taskUnit.configs.length > 0) {
        for (let config of taskUnit.configs) {
          if (!config.awardsKey) {
            this.$message.error('多节点任务必须设置奖励Key')
            return false
          }
          if (config.checkParams <= 0) {
            this.$message.error('任务判定值必须大于0')
            return false
          }
        }
      }
      
      return true
    },
    
    // 验证排行组件数据
    validateRankData() {
      const rankUnit = this.formData.rankUnit
      if (!rankUnit) return true
      
      if (!this.useDefaultRankTime) {
        if (!this.rankStartTime || !this.rankEndTime) {
          this.$message.error('自定义榜单时间不能为空')
          return false
        }
        if (this.rankStartTime >= this.rankEndTime) {
          this.$message.error('榜单开始时间必须早于结束时间')
          return false
        }
      }
      
      return true
    },
    
    // 统一数据验证
    validateFormData() {
      const unitType = this.formData.unitType
      
      // 基础验证
      if (!this.formData.unitName || !this.formData.unitAka) {
        this.$message.error('组件名称和别名不能为空')
        return false
      }
      
      // 根据组件类型进行特定验证
      switch (unitType) {
        case 1:
          // 验证货币组件
          if (!this.validateOnceMax()) {
            return false
          }
          return true
        case 2:
          return this.validateAwardPoolData()
        case 3:
          return this.validateTaskData()
        case 4:
          return this.validateRankData()
        case 5:
          return this.validateStepPoolData()
        case 6:
          return this.validateShopData()
        case 7:
          return this.validateCurrencyMergeData()
        default:
          return true
      }
    },
    
    handleSave() {
      // 添加调试信息
      console.log('保存数据前的formData:', JSON.parse(JSON.stringify(this.formData)))
      
      this.$refs.unitForm.validate((valid) => {
        if (valid && this.validateFormData()) {
          const submitData = this.formatDataForSubmit()
          console.log('格式化后的提交数据:', JSON.parse(JSON.stringify(submitData)))
          this.$emit('save', submitData)
          this.handleClose()
        }
      })
    },
    
    // 格式化提交数据，确保符合API规范
    formatDataForSubmit() {
      const submitData = JSON.parse(JSON.stringify(this.formData))
      // 根据组件类型，只保留对应的配置，其他设为null
      const unitType = submitData.unitType
      
      // 先将所有组件配置设为null
      if (unitType !== 1) submitData.currencyUnit = null
      if (unitType !== 2) submitData.awardPoolUnit = null  
      if (unitType !== 3) submitData.taskUnit = null
      if (unitType !== 4) submitData.rankUnit = null
      if (unitType !== 5) submitData.stepPoolUnit = null
      if (unitType !== 6) submitData.shopUnit = null
      if (unitType !== 7) submitData.currencyMergeUnit = null
      if (unitType !== 8) submitData.teamUnit = null
      if (unitType !== 9) submitData.shareUnit = null
      
      // 处理当前组件类型的特殊数据格式
      switch (unitType) {
        case 3: // 任务组件
          if (this.checkParamsSetStr && submitData.taskUnit) {
            submitData.taskUnit.checkParamsSet = this.parseIntArray(this.checkParamsSetStr)
            console.log("---");
            console.log(submitData.taskUnit.checkParamsSet);
          }
          break
          
        case 4: // 排行组件
          if (submitData.rankUnit) {
            // 处理checkParamsSet（字符串数组）
            if (this.rankCheckParamsStr) {
              submitData.rankUnit.checkParamsSet = this.parseStringArray(this.rankCheckParamsStr)
            }
            // 处理时间配置
            this.formatRankTimeData(submitData)
            // 保证noticeSupportChannel字段存在
            if (!submitData.rankUnit.noticeSupportChannel) {
              submitData.rankUnit.noticeSupportChannel = []
            }
            // 新增：确保排行组件新字段存在
            if (submitData.rankUnit.enableRankLabel === undefined) {
              submitData.rankUnit.enableRankLabel = false
            }
            if (!submitData.rankUnit.rankLabelConfigs) {
              submitData.rankUnit.rankLabelConfigs = []
            }
            if (submitData.rankUnit.rankDuration === undefined) {
              submitData.rankUnit.rankDuration = 0
            }
            if (submitData.rankUnit.enablePool === undefined) {
              submitData.rankUnit.enablePool = false
            }
            if (!submitData.rankUnit.rankPoolInfo) {
              submitData.rankUnit.rankPoolInfo = {
                poolRate: 0,
                basePoolAmount: '',
                maxPoolAmount: '',
                showRate: 0,
                rankPoolConfigs: []
              }
            }
            if (!submitData.rankUnit.rankPoolInfo.rankPoolConfigs) {
              submitData.rankUnit.rankPoolInfo.rankPoolConfigs = []
            }
          }
          break
          
        case 5: // 步频组件
          if (submitData.stepPoolUnit) {
            this.formatStepPoolData(submitData.stepPoolUnit)
          }
          break
          
        case 6: // 商店组件
          if (submitData.shopUnit) {
            this.formatShopData(submitData.shopUnit)
          }
          break
      }
      
      return submitData
    },
    
    // 解析整数数组
    parseIntArray(str) {
      return str.split(',')
        .map(param => parseInt(param.trim()))
        .filter(param => !isNaN(param))
    },
    
    // 解析字符串数组  
    parseStringArray(str) {
      return str.split(',')
        .map(param => param.trim())
        .filter(param => param)
    },
    
    // 格式化排行组件时间数据
    formatRankTimeData(submitData) {
      if (this.useDefaultRankTime) {
        // 使用活动默认时间，需要转换格式
        if (this.activityStartTime && this.activityEndTime) {
          // 将活动时间字符串转换为秒时间戳
          const startTimestamp = Math.floor(new Date(this.activityStartTime).getTime() / 1000)
          const endTimestamp = Math.floor(new Date(this.activityEndTime).getTime() / 1000)
          
          submitData.rankUnit.startTime = startTimestamp
          submitData.rankUnit.endTime = endTimestamp
        } else {
          // 如果没有活动时间，设置为0表示使用活动默认时间
          submitData.rankUnit.startTime = 0
          submitData.rankUnit.endTime = 0
        }
      } else {
        // 使用自定义时间，转换为秒
        submitData.rankUnit.startTime = this.rankStartTime ? Math.floor(this.rankStartTime / 1000) : 0
        submitData.rankUnit.endTime = this.rankEndTime ? Math.floor(this.rankEndTime / 1000) : 0
      }
    },
    
    // 格式化步频组件数据
    formatStepPoolData(stepPoolUnit) {
      // 确保基本字段为整数类型
      stepPoolUnit.maxSteps = parseInt(stepPoolUnit.maxSteps) || 0
      stepPoolUnit.refreshCost = parseInt(stepPoolUnit.refreshCost) || 0
      
      // 确保stepConfigs中的step字段为整数类型
      if (stepPoolUnit.stepConfigs) {
        stepPoolUnit.stepConfigs.forEach(config => {
          config.step = parseInt(config.step) || 0
          config.cost = parseInt(config.cost) || 0
          config.remainingCost = parseInt(config.remainingCost) || 0
        })
      }
      
      // 确保stepRewards中的step字段为整数类型
      if (stepPoolUnit.stepRewards) {
        stepPoolUnit.stepRewards.forEach(reward => {
          reward.step = parseInt(reward.step) || 0
        })
      }
    },
    
    // 格式化商店组件数据
    formatShopData(shopUnit) {
      if (shopUnit.items) {
        shopUnit.items.forEach(item => {
          item.price = parseInt(item.price) || 0
          item.weight = parseInt(item.weight) || 0
        })
      }
    },
    
    // 获取渠道列表数据
    async getChannelList() {
      try {
        const channelData = await getChannelList()
        this.channelList = channelData
      } catch (error) {
        console.error('获取渠道列表数据失败:', error)
        // 如果获取失败，使用默认数据
        this.channelList = [
          { value: 'cde', name: 'CDE' },
          { value: 'googleplay', name: 'Google Play' },
          { value: 'appstore', name: 'App Store' },
          { value: 'official', name: 'Official' }
        ]
      }
    },

    // === 新增：排行组件新字段相关方法 ===
    
    // 添加榜单标签配置
    addRankLabelConfig() {
      console.log('添加榜单标签配置')
      // 确保数组存在
      if (!this.formData.rankUnit.rankLabelConfigs) {
        this.$set(this.formData.rankUnit, 'rankLabelConfigs', [])
      }
      const newIndex = this.formData.rankUnit.rankLabelConfigs.length
      this.$set(this.formData.rankUnit.rankLabelConfigs, newIndex, {
        maxTop: 1,
        labelImg: '',
        weight: 0
      })
      
      // 自动展开新添加的项目
      this.$nextTick(() => {
        if (!this.activeRankLabels.includes(`label-${newIndex}`)) {
          this.activeRankLabels.push(`label-${newIndex}`)
        }
      })
      console.log('当前标签配置数量:', this.formData.rankUnit.rankLabelConfigs.length)
    },

    // 删除榜单标签配置
    removeRankLabelConfig(index) {
      if (this.formData.rankUnit.rankLabelConfigs) {
        this.formData.rankUnit.rankLabelConfigs.splice(index, 1)
        
        // 更新折叠状态
        this.activeRankLabels = this.activeRankLabels.filter(name => name !== `label-${index}`)
        // 重新索引后面的项目
        this.activeRankLabels = this.activeRankLabels.map(name => {
          const match = name.match(/label-(\d+)/)
          if (match) {
            const itemIndex = parseInt(match[1])
            return itemIndex > index ? `label-${itemIndex - 1}` : name
          }
          return name
        })
      }
    },

    // 切换榜单标签折叠状态
    toggleRankLabelsCollapse() {
      this.rankLabelsCollapsed = !this.rankLabelsCollapsed
      if (this.rankLabelsCollapsed) {
        this.activeRankLabels = []
      } else {
        const configs = this.formData.rankUnit.rankLabelConfigs || []
        this.activeRankLabels = configs.map((_, index) => `label-${index}`)
      }
    },

    // 添加榜单奖池分成配置
    addRankPoolConfig() {
      console.log('添加榜单奖池分成配置')
      // 确保 rankPoolInfo 存在
      if (!this.formData.rankUnit.rankPoolInfo) {
        this.$set(this.formData.rankUnit, 'rankPoolInfo', {
          poolRate: 0,
          basePoolAmount: '',
          maxPoolAmount: '',
          showRate: 0,
          rankPoolConfigs: []
        })
      }
      // 确保数组存在
      if (!this.formData.rankUnit.rankPoolInfo.rankPoolConfigs) {
        this.$set(this.formData.rankUnit.rankPoolInfo, 'rankPoolConfigs', [])
      }
      const newIndex = this.formData.rankUnit.rankPoolInfo.rankPoolConfigs.length
      this.$set(this.formData.rankUnit.rankPoolInfo.rankPoolConfigs, newIndex, {
        minTop: 1,
        minScore: 0,
        rate: 0
      })
      
      // 自动展开新添加的项目
      this.$nextTick(() => {
        if (!this.activeRankPoolConfigs.includes(`poolconfig-${newIndex}`)) {
          this.activeRankPoolConfigs.push(`poolconfig-${newIndex}`)
        }
      })
      console.log('当前分成配置数量:', this.formData.rankUnit.rankPoolInfo.rankPoolConfigs.length)
    },

    // 删除榜单奖池分成配置
    removeRankPoolConfig(index) {
      if (this.formData.rankUnit.rankPoolInfo && this.formData.rankUnit.rankPoolInfo.rankPoolConfigs) {
        this.formData.rankUnit.rankPoolInfo.rankPoolConfigs.splice(index, 1)
        
        // 更新折叠状态
        this.activeRankPoolConfigs = this.activeRankPoolConfigs.filter(name => name !== `poolconfig-${index}`)
        // 重新索引后面的项目
        this.activeRankPoolConfigs = this.activeRankPoolConfigs.map(name => {
          const match = name.match(/poolconfig-(\d+)/)
          if (match) {
            const itemIndex = parseInt(match[1])
            return itemIndex > index ? `poolconfig-${itemIndex - 1}` : name
          }
          return name
        })
      }
    },

    // 切换榜单奖池分成配置折叠状态
    toggleRankPoolConfigsCollapse() {
      this.rankPoolConfigsCollapsed = !this.rankPoolConfigsCollapsed
      if (this.rankPoolConfigsCollapsed) {
        this.activeRankPoolConfigs = []
      } else {
        const configs = this.formData.rankUnit.rankPoolInfo?.rankPoolConfigs || []
        this.activeRankPoolConfigs = configs.map((_, index) => `poolconfig-${index}`)
      }
    },

    // 上传榜单标签图片
    async uploadRankLabelImg(file) {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('cover', 0)
      formData.append('dir', '/label/rank/unit_event/')
      const { data: res } = await this.$axios.post(
        this.$store.getters.path.hostmgr_upload +
        '?username=' +
        this.$store.getters.username +
        '&token=' +
        this.$store.getters.token,
        formData
      )
      
      if (res.code === 0) {
        this.$message.success('标签图片上传成功')
        return { code: res.code, url: res.data }
      } else {
        this.$message.error('标签图片上传失败: ' + res.msg)
        return { code: res.code, url: '' }
      }
    },

    // 处理榜单标签开关变化
    handleRankLabelChange(value) {
      console.log('榜单标签开关变化:', value)
      this.$set(this.formData.rankUnit, 'enableRankLabel', value)
      
      // 如果开启标签功能，确保数组存在
      if (value && !this.formData.rankUnit.rankLabelConfigs) {
        this.$set(this.formData.rankUnit, 'rankLabelConfigs', [])
      }
    },

    // 处理榜单奖池开关变化
    handlePoolChange(value) {
      console.log('榜单奖池开关变化:', value)
      this.$set(this.formData.rankUnit, 'enablePool', value)
      
      // 如果开启奖池功能，确保相关对象存在
      if (value) {
        if (!this.formData.rankUnit.rankPoolInfo) {
          this.$set(this.formData.rankUnit, 'rankPoolInfo', {
            poolRate: 0,
            basePoolAmount: '',
            maxPoolAmount: '',
            showRate: 0,
            rankPoolConfigs: []
          })
        } else if (!this.formData.rankUnit.rankPoolInfo.rankPoolConfigs) {
          this.$set(this.formData.rankUnit.rankPoolInfo, 'rankPoolConfigs', [])
        }
      }
    },

    // 处理自动下发奖励开关变化
    handleAutoGiveAwardChange(value) {
      console.log('自动下发奖励开关变化:', value)
      
      // 如果关闭自动下发奖励，且队伍任务已开启，则不允许关闭
      if (!value && this.formData.taskUnit.teamTask) {
        this.$message.warning('队伍任务已开启，无法关闭自动下发奖励')
        // 恢复开关状态
        this.$nextTick(() => {
          this.$set(this.formData.taskUnit, 'autoGiveAward', true)
        })
        return
      }
      
      // 如果关闭自动下发奖励，同时关闭队伍任务
      if (!value) {
        this.$set(this.formData.taskUnit, 'teamTask', false)
        this.$set(this.formData.taskUnit, 'teamUnitId', 0)
      }
    },

    // 处理队伍任务开关变化
    handleTeamTaskChange(value) {
      console.log('队伍任务开关变化:', value)
      
      // 如果开启队伍任务，但自动下发奖励未开启，则不允许开启
      if (value && !this.formData.taskUnit.autoGiveAward) {
        this.$message.warning('请先开启自动下发奖励才能设置队伍任务')
        // 恢复开关状态
        this.$nextTick(() => {
          this.$set(this.formData.taskUnit, 'teamTask', false)
        })
        return
      }
      
      // 如果关闭队伍任务，清空队伍组件ID
      if (!value) {
        this.$set(this.formData.taskUnit, 'teamUnitId', 0)
      }
    },

    // 处理子榜单开关变化
    handleSonRankChange(value) {
      console.log('子榜单开关变化:', value)
      
      // 确保 sonRankConfig 对象存在
      if (value && !this.formData.rankUnit.sonRankConfig) {
        this.$set(this.formData.rankUnit, 'sonRankConfig', {
          showTop: 1,
          awardTop: 1,
          diffSex: false
        })
      }
    },
    
    // 格式化榜单奖池分成配置的排名范围显示
    formatRankPoolRange(config, index) {
      const configs = this.formData.rankUnit.rankPoolInfo.rankPoolConfigs || []
      // 确保配置按minTop排序
      const sortedConfigs = [...configs].sort((a, b) => a.minTop - b.minTop)
      const current = sortedConfigs[index]
      const next = index < sortedConfigs.length - 1 ? sortedConfigs[index + 1] : null
      
      // 如果是最后一个配置，显示到TOP值
      if (!next) {
        const topValue = this.formData.rankUnit?.showTop || 50
        if (current.minTop < topValue) {
          return `第${current.minTop}-${topValue}名`
        }
        return `第${current.minTop || '?'}名`
      }
      
      // 如果有下一个配置，显示到下一个配置的前一名
      if (current.minTop < next.minTop - 1) {
        return `第${current.minTop}-${next.minTop - 1}名`
      }
      return `第${current.minTop || '?'}名`
    },
    
    // 格式化榜单奖励配置的排名范围显示
    formatRankRewardRange(reward, index) {
      const configs = this.formData.rankUnit.rewardConfigs || []
      // 确保配置按minTop排序
      const sortedConfigs = [...configs].sort((a, b) => a.minTop - b.minTop)
      const current = sortedConfigs[index]
      const next = index < sortedConfigs.length - 1 ? sortedConfigs[index + 1] : null
      
      // 如果是最后一个配置，显示到TOP值
      if (!next) {
        const topValue = this.formData.rankUnit?.showTop || 50
        if (current.minTop < topValue) {
          return `第${current.minTop}-${topValue}名`
        }
        return `第${current.minTop || '?'}名`
      }
      
      // 如果有下一个配置，显示到下一个配置的前一名
      if (current.minTop < next.minTop - 1) {
        return `第${current.minTop}-${next.minTop - 1}名`
      }
      return `第${current.minTop || '?'}名`
    }
  },
  async mounted() {
    // 获取任务常量数据
    await this.getTaskConstants()
    // 获取队伍周期常量数据
    await this.getTeamDurationConstants()
    // 获取榜单类型常量
    await this.getRankTypeConstants()
    // 获取榜单周期类型常量
    await this.getRankCycleTypeConstants()
    // 获取渠道列表数据
    await this.getChannelList()
  }
}
</script>

<style scoped lang="scss">
:deep(.el-alert__title) {
  font-size: 20px;
}

.unit-editor-content {
  .form-section {
    margin-bottom: 20px;
  }
  
  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .el-button {
      margin-left: 10px;
    }
  }
  
  .import-dialog-content {
    .el-textarea__inner {
      font-family: 'Courier New', monospace;
      font-size: 12px;
      line-height: 1.4;
    }
    
    p {
      margin: 4px 0;
      line-height: 1.4;
    }
  }
}
  
  // 多配置通用样式
  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    
    h4 {
      margin: 0;
      font-size: 16px;
      color: #303133;
      font-weight: bold;
    }
    
    .header-actions {
      display: flex;
      gap: 8px;
      align-items: center;
    }
  }
  
  // 折叠组件通用样式
  .task-configs-summary,
  .rank-rewards-summary,
  .shop-items-summary {
    margin-bottom: 16px;
  }
  
  .task-configs-collapse,
  .rank-rewards-collapse,
  .shop-items-collapse {
    .collapse-title {
      display: flex;
      align-items: center;
      width: 100%;
      
      .config-index {
        font-weight: bold;
        color: #303133;
        margin-right: 12px;
      }
      
      .config-preview {
        flex: 1;
        color: #666;
        font-size: 13px;
      }
    }
    
    .collapse-content {
      padding: 16px;
      background-color: #fafafa;
      border-radius: 4px;
      margin-top: 8px;
      
      .el-form-item {
        margin-bottom: 16px;
      }
      
      .el-input-number {
        width: 100%;
      }
    }
    
    ::v-deep .el-collapse-item__header {
      padding: 12px 20px;
      background-color: #f8f9fa;
      border: 1px solid #e4e7ed;
      border-radius: 4px;
      margin-bottom: 2px;
      
      &:hover {
        background-color: #ecf5ff;
        border-color: #409eff;
      }
    }
    
    ::v-deep .el-collapse-item__content {
      padding: 0;
      border: none;
    }
    
    ::v-deep .el-collapse-item__wrap {
      border: none;
    }
    
    ::v-deep .el-collapse-item {
      border: none;
      margin-bottom: 8px;
    }
  }

  // 任务组件样式
  .task-config-section,
  .task-configs-section {
    margin-bottom: 20px;
    
    h4 {
      margin: 0 0 16px 0;
      color: #303133;
      font-size: 16px;
      font-weight: bold;
    }
  }

  .task-configs-list {
    .task-config-card {
      margin-bottom: 16px;
      border: 1px solid #e4e7ed;
      
      &:hover {
        border-color: #409eff;
        box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
      }
      
      .task-config-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        
        span {
          font-weight: bold;
          color: #303133;
        }
      }
      
      ::v-deep .el-card__body {
        padding: 16px;
      }
      
      .el-form-item {
        margin-bottom: 16px;
      }
      
      .el-input-number {
        width: 100%;
      }
    }
  }

  // 榜单奖励配置样式
  .rank-rewards-section {
    margin-top: 20px;
    
    .rank-rewards-list {
      .rank-reward-card {
        margin-bottom: 16px;
        border: 1px solid #e4e7ed;
        
        &:hover {
          border-color: #67c23a;
          box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
        }
        
        .rank-reward-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          
          span {
            font-weight: bold;
            color: #303133;
          }
        }
        
        ::v-deep .el-card__body {
          padding: 16px;
        }
        
        .el-form-item {
          margin-bottom: 16px;
        }
        
        .el-input-number {
          width: 100%;
        }
      }
    }
  }

  // 商店组件样式
  .shop-items-config {
    .items-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;
      
      span {
        font-weight: bold;
        font-size: 16px;
        color: #303133;
      }
    }
    
    .shop-items-list {
      .shop-item-card {
        margin-bottom: 16px;
        border: 1px solid #e4e7ed;
        
        &:hover {
          border-color: #409eff;
          box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
        }
        
        .shop-item-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          
          span {
            font-weight: bold;
            color: #303133;
          }
        }
        
        ::v-deep .el-card__body {
          padding: 16px;
        }
        
        .el-form-item {
          margin-bottom: 16px;
        }
        
        .el-input-number {
          width: 100%;
        }
      }
    }
  }

.dialog-footer {
  text-align: right;
}

/* 对话框滚动优化 */
::v-deep .unit-editor-dialog {
  .el-dialog__body {
    max-height: calc(100vh - 200px);
    overflow-y: auto;
    
    /* 优化滚动条样式 */
    &::-webkit-scrollbar {
      width: 6px;
    }
    
    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 3px;
      
      &:hover {
        background: #a1a1a1;
      }
    }
  }
}

/* 新增：排行组件新字段样式 */
.rank-label-config,
.rank-duration-config,
.rank-pool-config {
  margin-top: 20px;
  
  .section-header {
    h4, h5 {
      margin: 0 0 16px 0;
      color: #303133;
      font-size: 16px;
      font-weight: bold;
    }
  }
}

.rank-label-configs,
.rank-pool-configs {
  margin-top: 16px;
}

.rank-labels-collapse,
.rank-pool-configs-collapse {
  .collapse-title {
    display: flex;
    align-items: center;
    width: 100%;
    
    .config-index {
      font-weight: bold;
      color: #303133;
      margin-right: 12px;
    }
    
    .config-preview {
      flex: 1;
      color: #666;
      font-size: 13px;
    }
  }
  
  .collapse-content {
    padding: 16px;
    background-color: #fafafa;
    border-radius: 4px;
    margin-top: 8px;
    
    .el-form-item {
      margin-bottom: 16px;
    }
    
    .el-input-number {
      width: 100%;
    }
  }
  
  ::v-deep .el-collapse-item__header {
    padding: 12px 20px;
    background-color: #f8f9fa;
    border: 1px solid #e4e7ed;
    border-radius: 4px;
    margin-bottom: 2px;
    
    &:hover {
      background-color: #ecf5ff;
      border-color: #409eff;
    }
  }
  
  ::v-deep .el-collapse-item__content {
    padding: 0;
    border: none;
  }
  
  ::v-deep .el-collapse-item__wrap {
    border: none;
  }
  
  ::v-deep .el-collapse-item {
    border: none;
    margin-bottom: 8px;
  }
}

.rank-labels-summary,
.rank-pool-configs-summary {
  margin-bottom: 16px;
}

.dialog-footer {
  text-align: right;
}

/* 对话框滚动优化 */
::v-deep .unit-editor-dialog {
  /* 垂直居中 */
    margin: 0 auto !important;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  
  .el-dialog__body {
    max-height: calc(100vh - 200px);
    overflow-y: auto;
    
    /* 优化滚动条样式 */
    &::-webkit-scrollbar {
      width: 6px;
    }
    
    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 3px;
      
      &:hover {
        background: #a1a1a1;
      }
    }
  }
  
  .el-dialog__footer {
    border-top: 1px solid #e4e7ed;
    background-color: #fafafa;
    padding: 12px 20px;
  }
}

/* 解决在抽屉中打开对话框的层级问题 */
::v-deep .el-dialog__wrapper {
  z-index: 3000 !important;
}

::v-deep .el-overlay {
  z-index: 2999 !important;
}

/* 榜单数值万分率tooltip样式 */
::v-deep .rank-pool-tooltip {
  .el-tooltip__popper {
    white-space: pre-line;
    line-height: 1.5;
  }
}
</style> 